import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import * as XLSX from "xlsx";
import Plot from "react-plotly.js";
import {
  clearCache,
  fetchColorAbsorbancePerMmAtWavelength,
  fetchColorGlassPropertyModels,
  createColorTrialColorMapping,
  createColorIlluminant,
  createColorMeasurement,
  createColorMeasurementMethod,
  createColorObserver,
  createColorReflectionCorrectionModel,
  createColorTransmittanceMode,
  deleteColorTrialColorMapping,
  deleteColorIlluminant,
  deleteColorMeasurementMethod,
  deleteColorObserver,
  deleteColorReflectionCorrectionModel,
  deleteColorTransmittanceMode,
  fetchChemSettingsFe2O3,
  fetchColorGlassProperties,
  fetchColorIlluminants,
  fetchColorMeasurementCurves,
  fetchColorMeasurementDetail,
  fetchColorMeasurementIndex,
  fetchColorMeasurementMethods,
  fetchColorMeasurements,
  fetchColorMeasurementsByIds,
  fetchColorObservers,
  fetchColorReflectionCorrectionModels,
  fetchColorTrialColorMappings,
  fetchColorTransmittanceModes,
  fetchTrialDurations,
  deleteColorMeasurements,
  replaceColorMeasurementCurves,
  updateColorIlluminant,
  updateColorMeasurement,
  updateColorMeasurementMethod,
  updateColorObserver,
  updateColorReflectionCorrectionModel,
  updateColorTrialColorMapping,
  updateColorTransmittanceMode
} from "../api/postgrest";
import {
  buildColorProcessRequest,
  isColorProcessSuccess,
  parseColorProcessResponse
} from "../api/colorProcessing";
import NasLoginModal from "../components/NasLoginModal";
import { useEscapeStack } from "../hooks/useEscapeStack";
import { useNas } from "../context/NasContext";
import {
    ActiveTrial,
    ColorCurveKind,
    ColorCurvePoint,
    ColorGlassProperty,
    ColorGlassPropertyModel,
    ColorIlluminant,
  ColorIlluminantPayload,
  ColorMeasurement,
  ColorMeasurementDetail,
  ColorMeasurementMethod,
  ColorMeasurementMethodPayload,
  ColorMeasurementPayload,
  ColorObserver,
  ColorObserverPayload,
  ColorReflectionCorrectionModel,
  ColorReflectionCorrectionModelPayload,
  ColorTrialColorMapping,
  ColorTrialColorMappingPayload,
  ColorTransmittanceMode,
  ColorTransmittanceModePayload,
  TrialType
} from "../types";
import {
  formatAppDateTime,
  formatCurrentDateTime,
  formatDateFilterDisplay,
  normalizeDateFilterInput
} from "../utils/dateTime";
import {
  CHROMATICITY_LOCUS,
  CHROMATICITY_BACKGROUND_IMAGE,
  CHROMATICITY_LABEL_POINTS,
  COLOR_CURVE_LABELS,
  COLOR_TREND_METRICS,
  ColorFilters,
  ColorSortField,
  ColorSortState,
  DEFAULT_COLOR_CURVE_KIND,
  LAB_COLOR_PLANE_IMAGE,
  TrialDurationLike,
  applyColorFilters,
  defaultColorFilters,
  deriveActiveTrials,
  getCurvePoints,
  measurementDisplayColor,
  renderChromaticitySvg,
  renderLabSvg,
  renderLineChartSvg,
  sortColorMeasurements
} from "../utils/colorView";

const PAGE_SIZE = 12;
const NAS_COLOR_INPUT_DIR = "uv-vis";
const SYNC_PAUSE_POLL_MS = 250;
const AUTO_METHOD_ID = "__auto_measurement_method__";
const REFERENCE_FALLBACK_METHOD_NAME = "Amber";
const COMPARE_SWATCHES = [
  "#2f6bff",
  "#e4572e",
  "#0f9d8f",
  "#8b5cf6",
  "#c026d3",
  "#15803d",
  "#be123c",
  "#0369a1"
];
const COMPARE_MARKER_SYMBOLS = [
  "circle",
  "diamond",
  "square",
  "triangle-up",
  "cross",
  "triangle-down",
  "hexagon",
  "star"
];

type PageTab = "archive" | "trend" | "reports" | "admin";
type LogTone = "info" | "success" | "warn" | "error";
type SyncProgressState = {
  foldersTotal: number;
  foldersDone: number;
  currentFolder: string;
  filesTotal: number;
  filesDone: number;
  totalFiles: number;
  totalFilesDone: number;
};

type ThicknessManifestEntry = {
  thicknessTopMm: number | null;
  thicknessMiddleMm: number | null;
  thicknessBottomMm: number | null;
  thicknessMeanMm: number | null;
  inputFe2O3Percent: number | null;
  inputFeTotalPercent: number | null;
};

type SyncFileCandidate = {
  folderLabel: string;
  folderRelativePath: string;
  csvFileName: string;
  inputPath: string;
  relativePath: string;
  measurementKey: string;
  measurementLabel: string;
  thicknessManifestPath: string;
  isReference: boolean;
};

type SyncFolderBatch = {
  label: string;
  files: SyncFileCandidate[];
};

type ColorTrialDurationRow = TrialDurationLike & {
  id: number;
};

type ValidatedSyncFile = SyncFileCandidate & {
  manifestEntry: ThicknessManifestEntry;
};

type MissingThicknessManifestRow = {
  folder: string;
  relativePath: string;
  csvFileName: string;
  sampleKey: string;
  manifestPath: string;
};

type LogEntry = {
  message: string;
  tone: LogTone;
};

type AdminFieldConfig = {
  key: string;
  label: string;
  type: "text" | "textarea" | "number" | "checkbox" | "select";
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  step?: string;
};

type AdminColumn<Row> = {
  label: string;
  render: (row: Row) => React.ReactNode;
};

type ChemSettingFe2O3Row = Awaited<ReturnType<typeof fetchChemSettingsFe2O3>>[number];
type ColorOpticalPropertyRuntime = {
  id: string;
  code: string;
  name: string;
  model_kind: string;
  coefficient_jsonb: Record<string, unknown>;
};

const buildInitialSyncProgress = (): SyncProgressState => ({
  foldersTotal: 0,
  foldersDone: 0,
  currentFolder: "",
  filesTotal: 0,
  filesDone: 0,
  totalFiles: 0,
  totalFilesDone: 0
});

const LOG_TONE_COLORS: Record<LogTone, string> = {
  info: "var(--text-muted)",
  success: "var(--status-ok)",
  warn: "var(--status-warning)",
  error: "var(--status-error)"
};

const PLOT_FONT_FAMILY = '"IBM Plex Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif';
const REDOX_TIME_OVERLAY_ABSORBANCE_WAVELENGTH_NM = 400;
const REDOX_TIME_OVERLAY_ABSORBANCE_SCALE = 100;
const REDOX_TIME_OVERLAY_LABEL =
  "Fe2 / FeTotal + Fe2O3 input (XRF) + Fe2 + Fe total + Abs/mm@400 over time";
const COLOR_TRIAL_FILTERS_TO_HIDE = new Set([
  "Formingtrial vorbereiten",
  "Formgebung",
  "Wannenkühlung",
  "Nozzle",
  "Artikel",
  "Ausfallzeit",
  "Brenner Versuche",
  "Feuerfest Checkliste"
]);

function parseCssColor(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("#")) {
    const hex = trimmed.slice(1);
    if (hex.length === 3) {
      return {
        r: Number.parseInt(`${hex[0]}${hex[0]}`, 16),
        g: Number.parseInt(`${hex[1]}${hex[1]}`, 16),
        b: Number.parseInt(`${hex[2]}${hex[2]}`, 16)
      };
    }
    if (hex.length === 6) {
      return {
        r: Number.parseInt(hex.slice(0, 2), 16),
        g: Number.parseInt(hex.slice(2, 4), 16),
        b: Number.parseInt(hex.slice(4, 6), 16)
      };
    }
    return null;
  }
  const match = trimmed.match(/rgba?\(([^)]+)\)/i);
  if (!match) return null;
  const channels = match[1].split(",").map((part) => Number.parseFloat(part.trim()));
  if (channels.length < 3 || channels.some((channel, index) => index < 3 && !Number.isFinite(channel))) {
    return null;
  }
  return { r: channels[0], g: channels[1], b: channels[2] };
}

function withAlpha(color: string, alpha: number, fallback: string) {
  const parsed = parseCssColor(color);
  if (!parsed) return fallback;
  return `rgba(${Math.round(parsed.r)}, ${Math.round(parsed.g)}, ${Math.round(parsed.b)}, ${alpha})`;
}

function getPlotTheme() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return {
      textPrimary: "#ecf1ff",
      textMuted: "#b9c7de",
      surface: "#172134",
      surfaceElevated: "#0f1a2c",
      surfaceBorder: "#30486a",
      divider: "#2a3f5f",
      accentOne: "#4fb5ff",
      accentTwo: "#6be7c5",
      accentThree: "#7cc7ff"
    };
  }
  const styles = window.getComputedStyle(document.documentElement);
  const read = (name: string, fallback: string) => styles.getPropertyValue(name).trim() || fallback;
  return {
    textPrimary: read("--text-primary", "#ecf1ff"),
    textMuted: read("--text-muted", "#b9c7de"),
    surface: read("--surface", "#172134"),
    surfaceElevated: read("--surface-elevated", "#0f1a2c"),
    surfaceBorder: read("--surface-border", "#30486a"),
    divider: read("--divider", "#2a3f5f"),
    accentOne: read("--accent-one", "#4fb5ff"),
    accentTwo: read("--accent-two", "#6be7c5"),
    accentThree: read("--accent-three", "#7cc7ff")
  };
}

function buildProfessionalPlotCardStyle(): CSSProperties {
  const theme = getPlotTheme();
  return {
    background: [
      `radial-gradient(circle at top right, ${withAlpha(theme.accentOne, 0.16, "rgba(79,181,255,0.16)")} 0%, transparent 42%)`,
      `radial-gradient(circle at bottom left, ${withAlpha(theme.accentTwo, 0.14, "rgba(107,231,197,0.14)")} 0%, transparent 44%)`,
      `linear-gradient(180deg, ${withAlpha(theme.surface, 0.96, "rgba(23,33,52,0.96)")} 0%, ${withAlpha(theme.surfaceElevated, 0.98, "rgba(15,26,44,0.98)")} 100%)`
    ].join(", "),
    border: `1px solid ${withAlpha(theme.surfaceBorder, 0.74, "rgba(48,72,106,0.74)")}`,
    boxShadow: `inset 0 1px 0 ${withAlpha(theme.textPrimary, 0.06, "rgba(255,255,255,0.06)")}, 0 10px 24px ${withAlpha(theme.surfaceElevated, 0.28, "rgba(15,23,42,0.28)")}`
  };
}

function sanitizeTrialSelections(trials: ColorFilters["trials"]): ColorFilters["trials"] {
  const next = { ...trials };
  COLOR_TRIAL_FILTERS_TO_HIDE.forEach((trialType) => {
    if (next[trialType]?.length) {
      next[trialType] = [];
    } else if (!(trialType in next)) {
      next[trialType] = [];
    }
  });
  return next;
}

function sanitizeColorFilters(filters: ColorFilters): ColorFilters {
  return {
    ...filters,
    glassColors: filters.glassColors || [],
    trials: sanitizeTrialSelections(filters.trials)
  };
}

function joinNasPath(...parts: Array<string | undefined>) {
  const cleaned = parts
    .filter(Boolean)
    .map((part, index) => {
      const value = String(part);
      if (index === 0) return value.replace(/[\\/]+$/, "");
      return value.replace(/^[\\/]+|[\\/]+$/g, "");
    });
  return cleaned.join("/");
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function normalizeCsvRelativePath(value: string) {
  return value.replace(/\\/g, "/").replace(/^\/+/, "");
}

function stemFromName(fileName: string) {
  return fileName.replace(/\.[^.]+$/, "");
}

function normalizeMeasurementKeyForThickness(value: string) {
  const normalized = String(value || "").trim().replace(/\\/g, "/");
  const leaf = normalized.includes("/") ? normalized.split("/").pop() || "" : normalized;
  return stemFromName(leaf).trim();
}

function normalizeMeasurementLabelKey(value: string) {
  return String(value || "").trim().toLowerCase();
}

function buildMeasurementLabel(relativePath: string, isReference: boolean) {
  if (!isReference) {
    const fileName = relativePath.split("/").pop() || relativePath;
    return normalizeMeasurementKeyForThickness(fileName);
  }
  return normalizeCsvRelativePath(relativePath).replace(/\.csv$/i, "").replace(/[\\/]+/g, "__");
}

function normalizeManifestColumnName(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[ _\-%()[\].]/g, "");
}

function findManifestColumn(
  normalizedColumns: Record<string, string>,
  aliases: string[]
) {
  for (const alias of aliases) {
    if (normalizedColumns[alias]) {
      return normalizedColumns[alias];
    }
  }
  return null;
}

function parseOptionalManifestNumber(value: unknown) {
  if (value == null) return null;
  const raw = String(value).trim().replace(",", ".");
  if (!raw) return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
}

function deriveMeanThickness(entry: {
  thicknessTopMm: number | null;
  thicknessMiddleMm: number | null;
  thicknessBottomMm: number | null;
  thicknessMeanMm: number | null;
}) {
  if (entry.thicknessMeanMm != null) return entry.thicknessMeanMm;
  if (
    entry.thicknessTopMm != null &&
    entry.thicknessMiddleMm != null &&
    entry.thicknessBottomMm != null
  ) {
    return (entry.thicknessTopMm + entry.thicknessMiddleMm + entry.thicknessBottomMm) / 3;
  }
  return null;
}

function parseThicknessManifestBase64(
  base64: string,
  manifestPath: string
) {
  const workbook = XLSX.read(base64, { type: "base64" });
  const entries = new Map<string, ThicknessManifestEntry>();

  workbook.SheetNames.forEach((sheetName) => {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) return;
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
      defval: null,
      raw: false
    });
    if (!rows.length) return;

    const columns = Object.keys(rows[0] || {});
    const normalizedColumns = Object.fromEntries(
      columns.map((column) => [normalizeManifestColumnName(column), column])
    );

    const sampleColumn = findManifestColumn(normalizedColumns, [
      "sample",
      "filename",
      "name",
      "measurement",
      "measurementname",
      "sampleid",
      "samplename",
      "file",
      "filepath"
    ]);
    if (!sampleColumn) return;

    const topColumn = findManifestColumn(normalizedColumns, ["top", "thicknesstop", "topthickness"]);
    const middleColumn = findManifestColumn(normalizedColumns, [
      "middle",
      "mid",
      "thicknessmiddle",
      "middlethickness",
      "center"
    ]);
    const bottomColumn = findManifestColumn(normalizedColumns, [
      "bottom",
      "thicknessbottom",
      "bottomthickness"
    ]);
    const meanColumn = findManifestColumn(normalizedColumns, [
      "mean",
      "avg",
      "average",
      "thicknessmean",
      "meanthickness"
    ]);
    const fe2o3Column = findManifestColumn(normalizedColumns, [
      "fe2o3",
      "fe2o3percent",
      "inputfe2o3",
      "inputfe2o3percent"
    ]);
    const feTotalColumn = findManifestColumn(normalizedColumns, [
      "fetotal",
      "fetotalpercent",
      "inputfetotal",
      "inputfetotalpercent"
    ]);

    rows.forEach((row) => {
      const key = normalizeMeasurementKeyForThickness(String(row[sampleColumn] || ""));
      if (!key) return;
      const next: ThicknessManifestEntry = {
        thicknessTopMm: topColumn ? parseOptionalManifestNumber(row[topColumn]) : null,
        thicknessMiddleMm: middleColumn ? parseOptionalManifestNumber(row[middleColumn]) : null,
        thicknessBottomMm: bottomColumn ? parseOptionalManifestNumber(row[bottomColumn]) : null,
        thicknessMeanMm: meanColumn ? parseOptionalManifestNumber(row[meanColumn]) : null,
        inputFe2O3Percent: fe2o3Column ? parseOptionalManifestNumber(row[fe2o3Column]) : null,
        inputFeTotalPercent: feTotalColumn ? parseOptionalManifestNumber(row[feTotalColumn]) : null
      };
      next.thicknessMeanMm = deriveMeanThickness(next);
      entries.set(key, next);
    });
  });

  if (!entries.size) {
    throw new Error(
      `No usable thickness rows found in ${manifestPath}. Expected columns like sample + mean/top/middle/bottom.`
    );
  }
  return entries;
}

function escapeCsvCell(value: string) {
  const escaped = value.replace(/"/g, "\"\"");
  return /[",\n\r]/.test(escaped) ? `"${escaped}"` : escaped;
}

function formatManifestCsvNumber(value: number | null | undefined) {
  return Number.isFinite(value) ? String(value) : "";
}

function buildThicknessManifestCsvText(files: ValidatedSyncFile[]) {
  const header = ["sample", "top", "middle", "bottom", "mean", "fe2o3"];
  const rows = files.map((file) => {
    const entry = file.manifestEntry;
    return [
      file.measurementKey,
      formatManifestCsvNumber(entry.thicknessTopMm),
      formatManifestCsvNumber(entry.thicknessMiddleMm),
      formatManifestCsvNumber(entry.thicknessBottomMm),
      formatManifestCsvNumber(entry.thicknessMeanMm),
      formatManifestCsvNumber(entry.inputFe2O3Percent)
    ];
  });
  return [header, ...rows]
    .map((cells) => cells.map((cell) => escapeCsvCell(String(cell))).join(","))
    .join("\n");
}

function buildMissingThicknessRowsCsvText(rows: MissingThicknessManifestRow[]) {
  const header = ["folder", "relative_path", "csv_file", "sample_key", "thickness_manifest_path"];
  return [header, ...rows.map((row) => [row.folder, row.relativePath, row.csvFileName, row.sampleKey, row.manifestPath])]
    .map((cells) => cells.map((cell) => escapeCsvCell(String(cell))).join(","))
    .join("\n");
}

function compactWorkerDetail(value: unknown, maxLength = 220) {
  if (value == null) return "";
  const text = typeof value === "string" ? value : JSON.stringify(value);
  if (!text) return "";
  const compact = text.replace(/\s+/g, " ").trim();
  if (!compact) return "";
  return compact.length > maxLength ? `${compact.slice(0, maxLength - 3)}...` : compact;
}

function formatWorkerProcessingError(
  code: string,
  message: string,
  details: Record<string, unknown>
) {
  const parts: string[] = [];
  const exceptionDetail = compactWorkerDetail(details.exception);
  const stderrDetail = compactWorkerDetail(details.stderr);
  if (exceptionDetail) parts.push(`exception=${exceptionDetail}`);
  if (stderrDetail) parts.push(`stderr=${stderrDetail}`);
  return parts.length ? `${code}: ${message} (${parts.join(" | ")})` : `${code}: ${message}`;
}

function resolveMeasurementObserver(row: ColorMeasurement) {
  return (row.observer_code || row.observer_name || row.applied_observer_id || "").trim();
}

function resolveMeasurementIlluminant(row: ColorMeasurement) {
  return (row.illuminant_code || row.illuminant_name || row.applied_illuminant_id || "").trim();
}

function summarizeViewingConditions(rows: ColorMeasurement[]) {
  const observers = Array.from(new Set(rows.map(resolveMeasurementObserver).filter(Boolean)));
  const illuminants = Array.from(new Set(rows.map(resolveMeasurementIlluminant).filter(Boolean)));
  const mixed = observers.length > 1 || illuminants.length > 1;
  const observerLabel = observers.length === 1 ? observers[0] : observers.length ? "Mixed" : "Unknown";
  const illuminantLabel =
    illuminants.length === 1 ? illuminants[0] : illuminants.length ? "Mixed" : "Unknown";
  const label = `${observerLabel} / ${illuminantLabel}`;
  const warning = mixed
    ? "Selected rows use different observer and/or illuminant settings. Direct chromaticity/LAB comparison is scientifically invalid."
    : null;
  return {
    observers,
    illuminants,
    mixed,
    label,
    warning
  };
}

function formatMetric(value: number | null | undefined, digits = 3, fallback = "—") {
  return Number.isFinite(value) ? Number(value).toFixed(digits) : fallback;
}

function formatPercent(value: number | null | undefined, digits = 2) {
  return Number.isFinite(value) ? `${Number(value).toFixed(digits)}%` : "—";
}

function formatWavelength(
  value: number | null | undefined,
  mode: ColorMeasurement["wavelength_mode"] = null
) {
  if (!Number.isFinite(value)) return "—";
  const suffix = mode === "complementary" ? " comp." : "";
  return `${Number(value).toFixed(1)} nm${suffix}`;
}

function formatReferenceLabel(row: ColorMeasurement) {
  if (row.measurement_label?.trim()) return row.measurement_label.trim();
  if (row.source_csv_path?.trim()) {
    const normalized = row.source_csv_path.replace(/\\/g, "/");
    return normalized.split("/").pop() || normalized;
  }
  return row.id;
}

function compareByTimeAscending(left: ColorMeasurement, right: ColorMeasurement) {
  const leftTime = Date.parse(left.measured_at);
  const rightTime = Date.parse(right.measured_at);
  return leftTime - rightTime;
}

function toFiniteNumber(value: unknown) {
  if (value == null) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function parseMeasurementTimestamp(fileName: string): Date | null {
  const trimmed = stemFromName(fileName).trim();
  const patterns = [
    /^(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})(?:-(\d{2}))?(?:[_-].*)?$/,
    /^(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})(?:-(\d{2}))?(?:[_-].*)?$/
  ];
  for (const pattern of patterns) {
    const match = pattern.exec(trimmed);
    if (!match) continue;
    const [, year, month, day, hour, minute, second] = match;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second || "0")
    );
  }
  return null;
}

function findNearestChemSetting(
  rows: ChemSettingFe2O3Row[],
  measuredAt: Date | null
): ChemSettingFe2O3Row | null {
  if (!measuredAt || !rows.length) return null;
  const measuredTime = measuredAt.getTime();
  let nearest: ChemSettingFe2O3Row | null = null;
  let nearestDelta = Number.POSITIVE_INFINITY;
  rows.forEach((row) => {
    const chemTime = Date.parse(row.timestamp);
    if (!Number.isFinite(chemTime)) return;
    const delta = Math.abs(chemTime - measuredTime);
    if (delta < nearestDelta) {
      nearest = row;
      nearestDelta = delta;
    }
  });
  return nearest;
}

function findActiveGlaschemieTrial(
  rows: ColorTrialDurationRow[],
  measuredAt: string | Date | null | undefined
): ColorTrialDurationRow | null {
  if (!measuredAt) return null;
  const measuredTime = measuredAt instanceof Date ? measuredAt.getTime() : Date.parse(measuredAt);
  if (!Number.isFinite(measuredTime)) return null;
  const active = rows
    .filter((row) => {
      if (row.trial_type.toLowerCase() !== "glaschemie") return false;
      const startTime = Date.parse(row.start_time);
      const endTime = row.end_time ? Date.parse(row.end_time) : Number.POSITIVE_INFINITY;
      return Number.isFinite(startTime) && measuredTime >= startTime && measuredTime <= endTime;
    })
    .sort((left, right) => Date.parse(right.start_time) - Date.parse(left.start_time));
  return active[0] || null;
}

function resolveGlassColorForMeasurement({
  isReference,
  measuredAt,
  trialDurations,
  mappingByTrialId
}: {
  isReference: boolean;
  measuredAt: string | Date | null | undefined;
  trialDurations: ColorTrialDurationRow[];
  mappingByTrialId: Map<number, ColorTrialColorMapping>;
}) {
  const activeGlaschemieTrial = isReference
    ? null
    : findActiveGlaschemieTrial(trialDurations, measuredAt);
  const mappedGlassColor = activeGlaschemieTrial
    ? mappingByTrialId.get(activeGlaschemieTrial.id)?.glass_color?.trim() || null
    : null;
  const usedReferenceFallback = isReference && !mappedGlassColor;
  return {
    activeGlaschemieTrial,
    mappedGlassColor,
    glassColor: mappedGlassColor || (usedReferenceFallback ? REFERENCE_FALLBACK_METHOD_NAME : null),
    usedReferenceFallback,
    glassColorTrialDurationId: activeGlaschemieTrial?.id ?? null
  };
}

function normalizeAutoMethodLookup(value: string | null | undefined) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function resolveAutoMeasurementMethod(
  methods: ColorMeasurementMethod[],
  glassColor: string | null
): ColorMeasurementMethod | null {
  const colorKey = normalizeAutoMethodLookup(glassColor);
  if (!colorKey) return null;
  const activeMethods = methods.filter((row) => row.is_active !== false);
  const exact = activeMethods.find((row) => normalizeAutoMethodLookup(row.name) === colorKey);
  if (exact) return exact;
  return (
    activeMethods.find((row) => {
      const methodKey = normalizeAutoMethodLookup(row.name);
      return methodKey.includes(colorKey) || colorKey.includes(methodKey);
    }) || null
  );
}

const FE_TOTAL_FROM_FE2O3_FACTOR = (55.845 * 2.0) / (55.845 * 2.0 + 16.0 * 3.0);

function feTotalPercentFromFe2O3(fe2o3Percent: number | null | undefined) {
  return Number.isFinite(fe2o3Percent)
    ? Number(fe2o3Percent) * FE_TOTAL_FROM_FE2O3_FACTOR
    : undefined;
}

function resolveFeTotalPercent(
  inputFeTotalPercent: number | null | undefined,
  inputFe2O3Percent: number | null | undefined
) {
  if (Number.isFinite(inputFeTotalPercent)) return Number(inputFeTotalPercent);
  return feTotalPercentFromFe2O3(inputFe2O3Percent);
}

function resolveOpticalPropertyCode(csvPath: string) {
  const lowered = csvPath.toLowerCase();
  if (lowered.includes("dimethylester")) {
    return "quartz_glass";
  }
  return "sls_glass";
}

function displayColorForMeasurement(
  measurement: Pick<ColorMeasurement, "l_star" | "a_star" | "b_star" | "x_chromaticity" | "y_chromaticity">,
  fallback = "#225145"
) {
  return measurementDisplayColor(measurement, fallback);
}

function buildChromaticityPlotData(
  rows: ColorMeasurement[],
  referenceId: string | null,
  labelMode: "auto" | "always" | "never" = "auto"
) {
  const theme = getPlotTheme();
  const plotted = rows
    .filter((row) => Number.isFinite(row.x_chromaticity) && Number.isFinite(row.y_chromaticity))
    .map((row, index) => ({
      id: row.id,
      label: formatReferenceLabel(row),
      x: row.x_chromaticity as number,
      y: row.y_chromaticity as number,
      color: colorForIndex(index),
      symbol: markerSymbolForIndex(index)
    }));

  const reference = plotted.find((row) => row.id === referenceId) || null;
  const others = plotted.filter((row) => row.id !== referenceId);

  return [
    {
      x: CHROMATICITY_LOCUS.map((point) => point.x),
      y: CHROMATICITY_LOCUS.map((point) => point.y),
      type: "scatter",
      mode: "lines",
      name: "Spectral locus",
      hoverinfo: "skip",
      line: { color: withAlpha(theme.textPrimary, 0.74, "rgba(236,241,255,0.74)"), width: 1.6 }
    },
    {
      x: [CHROMATICITY_LOCUS[0].x, CHROMATICITY_LOCUS[CHROMATICITY_LOCUS.length - 1].x],
      y: [CHROMATICITY_LOCUS[0].y, CHROMATICITY_LOCUS[CHROMATICITY_LOCUS.length - 1].y],
      type: "scatter",
      mode: "lines",
      name: "Purple boundary",
      hoverinfo: "skip",
      line: { color: withAlpha(theme.textMuted, 0.55, "rgba(185,199,222,0.55)"), width: 1.1 }
    },
    {
      x: CHROMATICITY_LABEL_POINTS.map((point) => point.x),
      y: CHROMATICITY_LABEL_POINTS.map((point) => point.y),
      text: CHROMATICITY_LABEL_POINTS.map((point) => String(point.wavelength_nm)),
      type: "scatter",
      mode: "text",
      showlegend: false,
      hoverinfo: "skip",
      textfont: { size: 10, color: withAlpha(theme.textMuted, 0.9, "rgba(185,199,222,0.9)") }
    },
    others.length
      ? {
          x: others.map((row) => row.x),
          y: others.map((row) => row.y),
          type: "scatter",
          mode: "markers",
          name: "Measurements",
          text: others.map((row) => row.label),
          hovertemplate: "%{text}<br>x=%{x:.4f}<br>y=%{y:.4f}<extra></extra>",
          marker: {
            size: 10,
            color: others.map((row) => row.color),
            symbol: others.map((row) => row.symbol),
            line: { color: withAlpha(theme.surface, 0.98, "rgba(12,18,32,0.98)"), width: 1.8 }
          }
        }
      : null,
    reference
      ? {
          x: [reference.x],
          y: [reference.y],
          type: "scatter",
          mode: "markers",
          name: "Selected measurement",
          text: [reference.label],
          hovertemplate: "%{text}<br>x=%{x:.4f}<br>y=%{y:.4f}<extra></extra>",
          marker: {
            size: 14,
            color: reference.color,
            symbol: "star",
            line: { color: withAlpha(theme.surface, 0.98, "rgba(12,18,32,0.98)"), width: 2.4 }
          }
        }
      : null
  ].filter(Boolean);
}

function buildChromaticityAnnotations(
  rows: ColorMeasurement[],
  referenceId: string | null,
  labelMode: "auto" | "always" | "never" = "auto"
) {
  const theme = getPlotTheme();
  const plotted = rows
    .filter((row) => Number.isFinite(row.x_chromaticity) && Number.isFinite(row.y_chromaticity))
    .map((row, index) => ({
      id: row.id,
      label: formatReferenceLabel(row),
      x: row.x_chromaticity as number,
      y: row.y_chromaticity as number,
      color: colorForIndex(index)
    }));
  const showLabels = labelMode === "always" || (labelMode === "auto" && plotted.length <= 3);
  if (!showLabels) return [];
  return plotted.map((row, index) => {
    const highlighted = row.id === referenceId;
    const xDirection = row.x > 0.58 ? -1 : 1;
    const yDirection = index % 2 === 0 ? -1 : 1;
    return {
      x: row.x,
      y: row.y,
      text: row.label,
      showarrow: true,
      arrowhead: 0,
      ax: xDirection * 18,
      ay: yDirection * 18,
      xanchor: xDirection === 1 ? "left" : "right",
      yanchor: yDirection === 1 ? "bottom" : "top",
      bgcolor: withAlpha(theme.surface, 0.96, "rgba(23,33,52,0.96)"),
      bordercolor: row.color,
      borderwidth: highlighted ? 1.6 : 1,
      borderpad: 4,
      arrowcolor: row.color,
      font: {
        family: PLOT_FONT_FAMILY,
        size: highlighted ? 12 : 11,
        color: theme.textPrimary
      },
      opacity: 0.98
    };
  });
}

function buildChromaticityLayout(
  rows: ColorMeasurement[] = [],
  referenceId: string | null = null,
  labelMode: "auto" | "always" | "never" = "auto"
) {
  const theme = getPlotTheme();
  return {
    autosize: true,
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: withAlpha(theme.surfaceElevated, 0.86, "rgba(15,26,44,0.86)"),
    font: { family: PLOT_FONT_FAMILY, color: theme.textPrimary, size: 12 },
    margin: { l: 52, r: 20, t: 20, b: 40 },
    images: [
      {
        source: CHROMATICITY_BACKGROUND_IMAGE,
        xref: "x",
        yref: "y",
        x: 0,
        y: 0.9,
        sizex: 0.8,
        sizey: 0.9,
        xanchor: "left",
        yanchor: "top",
        sizing: "stretch",
        layer: "below",
        opacity: 1
      }
    ],
    xaxis: {
      title: "x",
      range: [-0.05, 0.85],
      showgrid: false,
      zeroline: true,
      zerolinecolor: withAlpha(theme.textPrimary, 0.9, "rgba(236,241,255,0.9)"),
      zerolinewidth: 1.5,
      showline: true,
      linecolor: withAlpha(theme.textPrimary, 0.7, "rgba(236,241,255,0.7)")
    },
    yaxis: {
      title: "y",
      range: [-0.1, 0.9],
      showgrid: false,
      zeroline: true,
      zerolinecolor: withAlpha(theme.textPrimary, 0.9, "rgba(236,241,255,0.9)"),
      zerolinewidth: 1.5,
      showline: true,
      linecolor: withAlpha(theme.textPrimary, 0.7, "rgba(236,241,255,0.7)"),
      scaleanchor: "x",
      scaleratio: 1
    },
    legend: {
      orientation: "h",
      y: -0.24,
      x: 0,
      bgcolor: withAlpha(theme.surface, 0.82, "rgba(23,33,52,0.82)")
    },
    hoverlabel: {
      bgcolor: withAlpha(theme.surfaceElevated, 0.98, "rgba(15,26,44,0.98)"),
      bordercolor: withAlpha(theme.accentOne, 0.6, "rgba(79,181,255,0.6)"),
      font: { family: PLOT_FONT_FAMILY, color: theme.textPrimary, size: 12 }
    },
    annotations: buildChromaticityAnnotations(rows, referenceId, labelMode)
  };
}

function buildLabPlotData(
  rows: ColorMeasurement[],
  referenceId: string | null,
  labelMode: "auto" | "always" | "never" = "auto"
) {
  const theme = getPlotTheme();
  const plotted = rows
    .filter(
      (row) =>
        Number.isFinite(row.l_star) &&
        Number.isFinite(row.a_star) &&
        Number.isFinite(row.b_star)
    )
    .map((row, index) => ({
      id: row.id,
      label: formatReferenceLabel(row),
      l: row.l_star as number,
      a: row.a_star as number,
      b: row.b_star as number,
      color: colorForIndex(index),
      symbol: markerSymbolForIndex(index)
    }));
  const reference = plotted.find((row) => row.id === referenceId) || null;
  const others = plotted.filter((row) => row.id !== referenceId);

  return [
    {
      x: [0],
      y: [0],
      type: "scatter",
      mode: "markers",
      name: "Neutral",
      hovertemplate: "Neutral point<extra></extra>",
      marker: {
        size: 7,
        color: theme.accentOne,
        line: { color: theme.textPrimary, width: 1.2 }
      }
    },
    others.length
      ? {
          x: others.map((row) => row.a),
          y: others.map((row) => row.b),
          customdata: others.map((row) => row.l),
          type: "scatter",
          mode: "markers",
          name: "Measurements",
          text: others.map((row) => row.label),
          hovertemplate: "%{text}<br>L*=%{customdata:.2f}<br>a*=%{x:.2f}<br>b*=%{y:.2f}<extra></extra>",
          marker: {
            size: 11,
            color: others.map((row) => row.color),
            symbol: others.map((row) => row.symbol),
            line: { color: withAlpha(theme.surface, 0.98, "rgba(12,18,32,0.98)"), width: 1.8 }
          }
        }
      : null,
    reference
      ? {
          x: [reference.a],
          y: [reference.b],
          customdata: [reference.l],
          type: "scatter",
          mode: "markers",
          name: "Selected measurement",
          text: [reference.label],
          hovertemplate: "%{text}<br>L*=%{customdata:.2f}<br>a*=%{x:.2f}<br>b*=%{y:.2f}<extra></extra>",
          marker: {
            size: 15,
            color: reference.color,
            symbol: "star",
            line: { color: withAlpha(theme.surface, 0.98, "rgba(12,18,32,0.98)"), width: 2.4 }
          }
        }
      : null
  ].filter(Boolean);
}

function buildLabAnnotations(
  rows: ColorMeasurement[],
  referenceId: string | null,
  labelMode: "auto" | "always" | "never" = "auto"
) {
  const theme = getPlotTheme();
  const plotted = rows
    .filter(
      (row) =>
        Number.isFinite(row.l_star) &&
        Number.isFinite(row.a_star) &&
        Number.isFinite(row.b_star)
    )
    .map((row, index) => ({
      id: row.id,
      label: formatReferenceLabel(row),
      a: row.a_star as number,
      b: row.b_star as number,
      color: colorForIndex(index)
    }));
  const showLabels = labelMode === "always" || (labelMode === "auto" && plotted.length <= 3);
  if (!showLabels) return [];
  return plotted.map((row, index) => {
    const highlighted = row.id === referenceId;
    const xDirection = row.a > 20 ? -1 : 1;
    const yDirection = row.b > 0 || index % 2 === 0 ? -1 : 1;
    return {
      x: row.a,
      y: row.b,
      text: row.label,
      showarrow: true,
      arrowhead: 0,
      ax: xDirection * 18,
      ay: yDirection * 18,
      xanchor: xDirection === 1 ? "left" : "right",
      yanchor: yDirection === 1 ? "bottom" : "top",
      bgcolor: withAlpha(theme.surface, 0.96, "rgba(23,33,52,0.96)"),
      bordercolor: row.color,
      borderwidth: highlighted ? 1.6 : 1,
      borderpad: 4,
      arrowcolor: row.color,
      font: {
        family: PLOT_FONT_FAMILY,
        size: highlighted ? 12 : 11,
        color: theme.textPrimary
      },
      opacity: 0.98
    };
  });
}

function buildLabLayout(
  rows: ColorMeasurement[] = [],
  referenceId: string | null = null,
  labelMode: "auto" | "always" | "never" = "auto"
) {
  const theme = getPlotTheme();
  return {
    autosize: true,
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: withAlpha(theme.surfaceElevated, 0.86, "rgba(15,26,44,0.86)"),
    font: { family: PLOT_FONT_FAMILY, color: theme.textPrimary, size: 12 },
    margin: { l: 52, r: 20, t: 20, b: 40 },
    images: [
      {
        source: LAB_COLOR_PLANE_IMAGE,
        xref: "x",
        yref: "y",
        x: -120,
        y: 120,
        sizex: 240,
        sizey: 240,
        xanchor: "left",
        yanchor: "top",
        sizing: "stretch",
        layer: "below",
        opacity: 1
      }
    ],
    xaxis: {
      title: "a*",
      range: [-160, 160],
      showgrid: false,
      zeroline: true,
      zerolinecolor: withAlpha(theme.textPrimary, 0.9, "rgba(236,241,255,0.9)"),
      zerolinewidth: 1.5,
      showline: true,
      linecolor: withAlpha(theme.textPrimary, 0.7, "rgba(236,241,255,0.7)")
    },
    yaxis: {
      title: "b*",
      range: [-130, 130],
      showgrid: false,
      zeroline: true,
      zerolinecolor: withAlpha(theme.textPrimary, 0.9, "rgba(236,241,255,0.9)"),
      zerolinewidth: 1.5,
      showline: true,
      linecolor: withAlpha(theme.textPrimary, 0.7, "rgba(236,241,255,0.7)"),
      scaleanchor: "x",
      scaleratio: 1
    },
    legend: {
      orientation: "h",
      y: -0.24,
      x: 0,
      bgcolor: withAlpha(theme.surface, 0.82, "rgba(23,33,52,0.82)")
    },
    hoverlabel: {
      bgcolor: withAlpha(theme.surfaceElevated, 0.98, "rgba(15,26,44,0.98)"),
      bordercolor: withAlpha(theme.accentOne, 0.6, "rgba(79,181,255,0.6)"),
      font: { family: PLOT_FONT_FAMILY, color: theme.textPrimary, size: 12 }
    },
    annotations: buildLabAnnotations(rows, referenceId, labelMode)
  };
}

function buildSpectrumLayout(yTitle: string) {
  const theme = getPlotTheme();
  return {
    autosize: true,
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: withAlpha(theme.surfaceElevated, 0.86, "rgba(15,26,44,0.86)"),
    font: { family: PLOT_FONT_FAMILY, color: theme.textPrimary, size: 12 },
    margin: { l: 54, r: 24, t: 24, b: 46 },
    xaxis: {
      title: "Wavelength (nm)",
      gridcolor: withAlpha(theme.divider, 0.62, "rgba(42,63,95,0.62)"),
      showline: true,
      linecolor: withAlpha(theme.textPrimary, 0.64, "rgba(236,241,255,0.64)"),
      ticks: "outside",
      tickcolor: withAlpha(theme.textPrimary, 0.64, "rgba(236,241,255,0.64)")
    },
    yaxis: {
      title: yTitle,
      gridcolor: withAlpha(theme.divider, 0.62, "rgba(42,63,95,0.62)"),
      showline: true,
      linecolor: withAlpha(theme.textPrimary, 0.64, "rgba(236,241,255,0.64)"),
      ticks: "outside",
      tickcolor: withAlpha(theme.textPrimary, 0.64, "rgba(236,241,255,0.64)")
    },
    legend: {
      orientation: "h",
      y: -0.24,
      x: 0,
      bgcolor: withAlpha(theme.surface, 0.82, "rgba(23,33,52,0.82)")
    },
    hoverlabel: {
      bgcolor: withAlpha(theme.surfaceElevated, 0.98, "rgba(15,26,44,0.98)"),
      bordercolor: withAlpha(theme.accentOne, 0.6, "rgba(79,181,255,0.6)"),
      font: { family: PLOT_FONT_FAMILY, color: theme.textPrimary, size: 12 }
    }
  };
}

function buildTrendLayout(yTitle: string) {
  const theme = getPlotTheme();
  return {
    autosize: true,
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: withAlpha(theme.surfaceElevated, 0.86, "rgba(15,26,44,0.86)"),
    font: { family: PLOT_FONT_FAMILY, color: theme.textPrimary, size: 12 },
    margin: { l: 54, r: 24, t: 24, b: 50 },
    xaxis: {
      title: "Measured At",
      gridcolor: withAlpha(theme.divider, 0.62, "rgba(42,63,95,0.62)"),
      showline: true,
      linecolor: withAlpha(theme.textPrimary, 0.64, "rgba(236,241,255,0.64)")
    },
    yaxis: {
      title: yTitle,
      gridcolor: withAlpha(theme.divider, 0.62, "rgba(42,63,95,0.62)"),
      showline: true,
      linecolor: withAlpha(theme.textPrimary, 0.64, "rgba(236,241,255,0.64)")
    },
    legend: {
      orientation: "h",
      y: -0.22,
      x: 0,
      bgcolor: withAlpha(theme.surface, 0.82, "rgba(23,33,52,0.82)")
    },
    hoverlabel: {
      bgcolor: withAlpha(theme.surfaceElevated, 0.98, "rgba(15,26,44,0.98)"),
      bordercolor: withAlpha(theme.accentOne, 0.6, "rgba(79,181,255,0.6)"),
      font: { family: PLOT_FONT_FAMILY, color: theme.textPrimary, size: 12 }
    }
  };
}

function buildRedoxTimeOverlayPlot(
  rows: ColorMeasurement[],
  absorbancePerMmAt400ById: Record<string, number | null> = {}
) {
  const trendTheme = getPlotTheme();
  const sortedRows = [...rows].sort(compareByTimeAscending);
  const fe2FeTotalRows = sortedRows.filter((row) => toFiniteNumber(row.fe2_fe_total_percent) != null);
  const xrfFe2O3Rows = sortedRows.filter((row) => toFiniteNumber(row.input_fe2o3_percent) != null);
  const fe2PercentRows = sortedRows.filter((row) => toFiniteNumber(row.fe2_percent) != null);
  const feTotalPercentRows = sortedRows.filter((row) => toFiniteNumber(row.fe_total_percent) != null);
  const absorbancePerMm400Rows = sortedRows
    .map((row) => ({
      row,
      value: (() => {
        const rawValue = toFiniteNumber(absorbancePerMmAt400ById[row.id]);
        return rawValue == null ? null : rawValue * REDOX_TIME_OVERLAY_ABSORBANCE_SCALE;
      })()
    }))
    .filter((entry): entry is { row: ColorMeasurement; value: number } => entry.value != null);

  const base = buildTrendLayout("Fe2 / FeTotal (%)");
  const layout = {
    ...base,
    yaxis: {
      ...base.yaxis,
      title: `Fe2 / FeTotal (%) and A${REDOX_TIME_OVERLAY_ABSORBANCE_WAVELENGTH_NM} Abs/mm ×${REDOX_TIME_OVERLAY_ABSORBANCE_SCALE}`
    },
    yaxis2: {
      title: "Fe2O3 input / Fe2 / Fe total (%)",
      overlaying: "y",
      side: "right",
      showgrid: false,
      showline: true,
      linecolor: withAlpha(trendTheme.textPrimary, 0.64, "rgba(236,241,255,0.64)"),
      ticks: "outside",
      tickcolor: withAlpha(trendTheme.textPrimary, 0.64, "rgba(236,241,255,0.64)")
    }
  };

  const data = [
    {
      x: fe2FeTotalRows.map((row) => row.measured_at),
      y: fe2FeTotalRows.map((row) => toFiniteNumber(row.fe2_fe_total_percent) as number),
      text: fe2FeTotalRows.map((row) => formatReferenceLabel(row)),
      type: "scatter",
      mode: "lines+markers",
      name: "Fe2 / FeTotal",
      marker: {
        color: trendTheme.accentOne,
        size: 8,
        line: {
          color: withAlpha(trendTheme.textPrimary, 0.45, "rgba(236,241,255,0.45)"),
          width: 1.1
        }
      },
      line: {
        color: trendTheme.accentOne,
        width: 2.8,
        shape: "spline",
        smoothing: 0.45
      },
      hovertemplate: "%{text}<br>%{x}<br>Fe2/FeTotal: %{y:.4f}<extra></extra>",
      yaxis: "y"
    },
    {
      x: xrfFe2O3Rows.map((row) => row.measured_at),
      y: xrfFe2O3Rows.map((row) => toFiniteNumber(row.input_fe2o3_percent) as number),
      text: xrfFe2O3Rows.map((row) => formatReferenceLabel(row)),
      type: "scatter",
      mode: "lines+markers",
      name: "Fe2O3 input (XRF)",
      marker: {
        color: trendTheme.accentTwo,
        size: 8,
        symbol: "diamond",
        line: {
          color: withAlpha(trendTheme.textPrimary, 0.45, "rgba(236,241,255,0.45)"),
          width: 1.1
        }
      },
      line: {
        color: trendTheme.accentTwo,
        width: 2.6,
        shape: "spline",
        smoothing: 0.45,
        dash: "dot"
      },
      hovertemplate: "%{text}<br>%{x}<br>Fe2O3 input: %{y:.4f}<extra></extra>",
      yaxis: "y2"
    },
    {
      x: fe2PercentRows.map((row) => row.measured_at),
      y: fe2PercentRows.map((row) => toFiniteNumber(row.fe2_percent) as number),
      text: fe2PercentRows.map((row) => formatReferenceLabel(row)),
      type: "scatter",
      mode: "lines+markers",
      name: "Fe2 percent",
      marker: {
        color: "#ff8a00",
        size: 8,
        symbol: "square",
        line: {
          color: withAlpha(trendTheme.textPrimary, 0.45, "rgba(236,241,255,0.45)"),
          width: 1.1
        }
      },
      line: {
        color: "#ff8a00",
        width: 2.2,
        shape: "spline",
        smoothing: 0.45,
        dash: "dash"
      },
      hovertemplate: "%{text}<br>%{x}<br>Fe2 percent: %{y:.4f}<extra></extra>",
      yaxis: "y2"
    },
    {
      x: feTotalPercentRows.map((row) => row.measured_at),
      y: feTotalPercentRows.map((row) => toFiniteNumber(row.fe_total_percent) as number),
      text: feTotalPercentRows.map((row) => formatReferenceLabel(row)),
      type: "scatter",
      mode: "lines+markers",
      name: "Fe total percent",
      marker: {
        color: "#a855f7",
        size: 8,
        symbol: "circle-open",
        line: {
          color: withAlpha(trendTheme.textPrimary, 0.45, "rgba(236,241,255,0.45)"),
          width: 1.1
        }
      },
      line: {
        color: "#a855f7",
        width: 2.1,
        shape: "spline",
        smoothing: 0.45,
        dash: "dot"
      },
      hovertemplate: "%{text}<br>%{x}<br>Fe total percent: %{y:.4f}<extra></extra>",
      yaxis: "y2"
    },
    {
      x: absorbancePerMm400Rows.map((entry) => entry.row.measured_at),
      y: absorbancePerMm400Rows.map((entry) => entry.value),
      text: absorbancePerMm400Rows.map((entry) => formatReferenceLabel(entry.row)),
      type: "scatter",
      mode: "lines+markers",
      name: `Absorbance/mm @ ${REDOX_TIME_OVERLAY_ABSORBANCE_WAVELENGTH_NM} nm ×${REDOX_TIME_OVERLAY_ABSORBANCE_SCALE}`,
      marker: {
        color: "#f43f5e",
        size: 8,
        symbol: "triangle-up",
        line: {
          color: withAlpha(trendTheme.textPrimary, 0.45, "rgba(236,241,255,0.45)"),
          width: 1.1
        }
      },
      line: {
        color: "#f43f5e",
        width: 2.1,
        shape: "spline",
        smoothing: 0.45,
        dash: "longdash"
      },
      hovertemplate: `%{text}<br>%{x}<br>Abs/mm @ ${REDOX_TIME_OVERLAY_ABSORBANCE_WAVELENGTH_NM} nm ×${REDOX_TIME_OVERLAY_ABSORBANCE_SCALE}: %{y:.4f}<extra></extra>`,
      yaxis: "y"
    }
  ];

  return {
    hasData:
      fe2FeTotalRows.length > 0 ||
      xrfFe2O3Rows.length > 0 ||
      fe2PercentRows.length > 0 ||
      feTotalPercentRows.length > 0 ||
      absorbancePerMm400Rows.length > 0,
    layout,
    data
  };
}

function opticalPropertyTrendColor(code: string | null | undefined) {
  if (code === "quartz_glass") return "#4fb5ff";
  if (code === "sls_glass") return "#cc6633";
  return "#225145";
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function downloadText(content: string, fileName: string, type: string) {
  downloadBlob(new Blob([content], { type }), fileName);
}

function serializeForInlineScript(value: unknown) {
  return JSON.stringify(value).replace(/</g, "\\u003c");
}

function buildExportFileName(prefix: string, extension: string) {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `${prefix}_${stamp}.${extension}`;
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function renderHtmlTable(headers: string[], rows: string[][]) {
  const head = headers.map((header) => `<th>${escapeHtml(header)}</th>`).join("");
  const body = rows
    .map((cells) => `<tr>${cells.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
    .join("");
  return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

function colorForIndex(index: number) {
  return COMPARE_SWATCHES[index % COMPARE_SWATCHES.length];
}

function markerSymbolForIndex(index: number) {
  return COMPARE_MARKER_SYMBOLS[index % COMPARE_MARKER_SYMBOLS.length];
}

function isPresent<T>(value: T | null | undefined): value is T {
  return value != null;
}

export default function ColorPage() {
  const { status: nasStatus, mountPoint, error: nasError, config, setConfig, triggerMount } = useNas();

  const [tab, setTab] = useState<PageTab>("archive");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [measurementRows, setMeasurementRows] = useState<ColorMeasurement[]>([]);
  const [methodRows, setMethodRows] = useState<ColorMeasurementMethod[]>([]);
  const [observerRows, setObserverRows] = useState<ColorObserver[]>([]);
  const [illuminantRows, setIlluminantRows] = useState<ColorIlluminant[]>([]);
  const [transmittanceModeRows, setTransmittanceModeRows] = useState<ColorTransmittanceMode[]>([]);
  const [reflectionModelRows, setReflectionModelRows] = useState<ColorReflectionCorrectionModel[]>([]);
  const [glassPropertyRows, setGlassPropertyRows] = useState<ColorGlassProperty[]>([]);
  const [glassPropertyModelRows, setGlassPropertyModelRows] = useState<ColorGlassPropertyModel[]>([]);
  const [trialDurationRows, setTrialDurationRows] = useState<ColorTrialDurationRow[]>([]);
  const [colorTrialMappings, setColorTrialMappings] = useState<ColorTrialColorMapping[]>([]);

  const [filters, setFilters] = useState<ColorFilters>(defaultColorFilters);
  const [draftFilters, setDraftFilters] = useState<ColorFilters>(defaultColorFilters);
  const [searchTerm, setSearchTerm] = useState("");
  const [sort, setSort] = useState<ColorSortState>({
    field: "measured_at",
    direction: "desc"
  });
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [curveKind, setCurveKind] = useState<ColorCurveKind>(DEFAULT_COLOR_CURVE_KIND);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailCache, setDetailCache] = useState<Record<string, ColorMeasurementDetail>>({});
  const [detailLoadingIds, setDetailLoadingIds] = useState<Set<string>>(new Set());

  const [syncOpen, setSyncOpen] = useState(false);
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncLogs, setSyncLogs] = useState<LogEntry[]>([]);
  const [syncProgress, setSyncProgress] = useState<SyncProgressState>(buildInitialSyncProgress);
  const [syncDryRun, setSyncDryRun] = useState(false);
  const [syncClearExisting, setSyncClearExisting] = useState(false);
  const [syncMethodId, setSyncMethodId] = useState<string>(AUTO_METHOD_ID);
  const [syncFolderFilter, setSyncFolderFilter] = useState("");
  const [syncPaused, setSyncPaused] = useState(false);
  const [syncStopRequested, setSyncStopRequested] = useState(false);
  const [syncMissingRowsExporting, setSyncMissingRowsExporting] = useState(false);
  const syncControlRef = useRef({ pause: false, stop: false });

  const [nasCreds, setNasCreds] = useState(config);
  const [showNasLogin, setShowNasLogin] = useState(false);

  const needsLogin = nasStatus !== "mounted";

  const observerMap = useMemo(
    () => new Map(observerRows.map((row) => [row.id, row])),
    [observerRows]
  );
  const illuminantMap = useMemo(
    () => new Map(illuminantRows.map((row) => [row.id, row])),
    [illuminantRows]
  );
  const transmittanceModeMap = useMemo(
    () => new Map(transmittanceModeRows.map((row) => [row.id, row])),
    [transmittanceModeRows]
  );
  const reflectionModelMap = useMemo(
    () => new Map(reflectionModelRows.map((row) => [row.id, row])),
    [reflectionModelRows]
  );
  const glassPropertyMap = useMemo(
    () => new Map(glassPropertyRows.map((row) => [row.id, row])),
    [glassPropertyRows]
  );
  const defaultGlassPropertyModelMap = useMemo(
    () =>
      new Map(
        glassPropertyModelRows
          .filter((row) => row.is_default)
          .map((row) => [row.glass_property_id, row] as const)
      ),
    [glassPropertyModelRows]
  );
  const glassPropertyCodeMap = useMemo(
    () => new Map(glassPropertyRows.map((row) => [row.code, row] as const)),
    [glassPropertyRows]
  );

  const methods = useMemo<ColorMeasurementMethod[]>(
    () =>
      methodRows.map((row) => ({
        ...row,
        transmittance_mode_code: transmittanceModeMap.get(row.transmittance_mode_id)?.code,
        reflection_correction_code: reflectionModelMap.get(row.reflection_correction_model_id)?.code,
        observer_code: observerMap.get(row.observer_id)?.code,
        illuminant_code: illuminantMap.get(row.illuminant_id)?.code
      })),
    [illuminantMap, methodRows, observerMap, reflectionModelMap, transmittanceModeMap]
  );

  const methodMap = useMemo(() => new Map(methods.map((row) => [row.id, row])), [methods]);
  const colorTrialMappingByTrialId = useMemo(
    () => new Map(colorTrialMappings.map((row) => [row.trial_duration_id, row] as const)),
    [colorTrialMappings]
  );

  const measurements = useMemo<ColorMeasurement[]>(
    () =>
      measurementRows.map((row) => {
        const method = methodMap.get(row.measurement_method_id);
        const observer = observerMap.get(row.applied_observer_id);
        const illuminant = illuminantMap.get(row.applied_illuminant_id);
        const glassProperty = glassPropertyMap.get(row.glass_property_id);
        return {
          ...row,
          method_name: method?.name || row.method_name,
          observer_code: observer?.code || row.observer_code,
          observer_name: observer?.name || row.observer_name,
          illuminant_code: illuminant?.code || row.illuminant_code,
          illuminant_name: illuminant?.name || row.illuminant_name,
          glass_property_name: glassProperty?.name || row.glass_property_name,
          glass_property_code: glassProperty?.code || row.glass_property_code,
          active_trials: deriveActiveTrials(trialDurationRows, row.measured_at)
        };
      }),
    [glassPropertyMap, illuminantMap, measurementRows, methodMap, observerMap, trialDurationRows]
  );

  const measurementMap = useMemo<Map<string, ColorMeasurement>>(
    () => new Map(measurements.map((row) => [row.id, row] as const)),
    [measurements]
  );
  const effectiveFilters = useMemo(() => sanitizeColorFilters(filters), [filters]);
  const effectiveDraftFilters = useMemo(() => sanitizeColorFilters(draftFilters), [draftFilters]);

  const filtered = useMemo(
    () => sortColorMeasurements(applyColorFilters(measurements, effectiveFilters, searchTerm), sort),
    [effectiveFilters, measurements, searchTerm, sort]
  );

  const paginated = useMemo(
    () => filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [filtered, page]
  );

  const selectedMeasurements = useMemo(
    () =>
      Array.from(selectedIds)
        .map((id) => measurementMap.get(id))
        .filter(isPresent),
    [measurementMap, selectedIds]
  );

  const selectedDetails = useMemo(
    () =>
      selectedMeasurements
        .map((row) => detailCache[row.id])
        .filter(isPresent),
    [detailCache, selectedMeasurements]
  );

  const detailMeasurement = detailId ? measurementMap.get(detailId) || null : null;
  const detailRecord = detailId ? detailCache[detailId] || null : null;
  const detailLoading = detailId ? detailLoadingIds.has(detailId) : false;
  const reportBasis = selectedMeasurements.length ? selectedMeasurements : filtered;
  const reportDetailBasis = reportBasis
    .map((row) => detailCache[row.id])
    .filter(isPresent);

  const methodOptions = useMemo(
    () => methods.filter((row) => row.is_active !== false),
    [methods]
  );
  const glassColorOptions = useMemo(() => {
    const values = new Set<string>();
    colorTrialMappings.forEach((row) => {
      const value = row.glass_color?.trim();
      if (value) values.add(value);
    });
    measurementRows.forEach((row) => {
      const value = row.glass_color?.trim();
      if (value) values.add(value);
    });
    return Array.from(values).sort((left, right) => left.localeCompare(right));
  }, [colorTrialMappings, measurementRows]);

  const trialOptions = useMemo(() => {
    const base: Record<string, string[]> = {
      Glaschemie: [],
      Brennstoff: [],
      "Energieverhältnis": []
    };
    const map = new Map<string, Set<string>>();
    measurements.forEach((row) => {
      (row.active_trials || []).forEach((trial) => {
        if (COLOR_TRIAL_FILTERS_TO_HIDE.has(trial.trial_type)) return;
        const set = map.get(trial.trial_type) || new Set<string>();
        set.add(trial.descriptive_name);
        map.set(trial.trial_type, set);
      });
    });
    map.forEach((values, key) => {
      base[key] = Array.from(values).sort();
    });
    return Object.fromEntries(Object.entries(base).filter(([, values]) => values.length));
  }, [measurements]);

  useEffect(() => {
    loadAllData().catch((err) => {
      console.error(err);
    });
  }, []);

  useEffect(() => {
    setPage(0);
  }, [effectiveFilters, searchTerm, sort]);

  const loadAllData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [
        nextMeasurements,
        nextMethods,
        nextObservers,
        nextIlluminants,
        nextTransmittanceModes,
        nextReflectionModels,
        nextGlassProperties,
        nextGlassPropertyModels,
        nextTrialDurations,
        nextColorTrialMappings
      ] = await Promise.all([
        fetchColorMeasurements(),
        fetchColorMeasurementMethods(),
        fetchColorObservers(),
        fetchColorIlluminants(),
        fetchColorTransmittanceModes(),
        fetchColorReflectionCorrectionModels(),
        fetchColorGlassProperties(),
        fetchColorGlassPropertyModels(),
        fetchTrialDurations(),
        fetchColorTrialColorMappings()
      ]);
      setMeasurementRows(nextMeasurements);
      setMethodRows(nextMethods);
      setObserverRows(nextObservers);
      setIlluminantRows(nextIlluminants);
      setTransmittanceModeRows(nextTransmittanceModes);
      setReflectionModelRows(nextReflectionModels);
      setGlassPropertyRows(nextGlassProperties);
      setGlassPropertyModelRows(nextGlassPropertyModels);
      setTrialDurationRows(nextTrialDurations);
      setColorTrialMappings(nextColorTrialMappings);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load Color data");
    } finally {
      setLoading(false);
    }
  };

  const pushSyncLog = (message: string, tone: LogTone = "info") => {
    setSyncLogs((prev) => [...prev, { message, tone }].slice(-400));
  };

  const resetView = () => {
    setFilters(defaultColorFilters);
    setDraftFilters(defaultColorFilters);
    setSearchTerm("");
    setSort({ field: "measured_at", direction: "desc" });
    setSelectedIds(new Set());
    setCurveKind(DEFAULT_COLOR_CURVE_KIND);
    setPage(0);
  };

  const ensureDetails = async (ids: string[]) => {
    const uniqueIds = Array.from(new Set(ids)).filter((id) => !detailCache[id]);
    if (!uniqueIds.length) return;
    setDetailLoadingIds((prev) => new Set([...prev, ...uniqueIds]));
    try {
      const [rows, curveMap] = await Promise.all([
        fetchColorMeasurementsByIds(uniqueIds),
        fetchColorMeasurementCurves(uniqueIds)
      ]);
      setDetailCache((prev) => {
        const next = { ...prev };
        rows.forEach((row) => {
          const baseMeasurement = measurementMap.get(row.id) || row;
          next[row.id] = {
            ...baseMeasurement,
            curves: curveMap[row.id] || {}
          };
        });
        return next;
      });
    } finally {
      setDetailLoadingIds((prev) => {
        const next = new Set(prev);
        uniqueIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const openDetail = async (id: string) => {
    setDetailId(id);
    if (!detailCache[id]) {
      try {
        setDetailLoadingIds((prev) => new Set(prev).add(id));
        const detail = await fetchColorMeasurementDetail(id);
        if (detail) {
          const baseMeasurement = measurementMap.get(id) || detail;
          setDetailCache((prev) => ({
            ...prev,
            [id]: {
              ...baseMeasurement,
              curves: detail.curves
            }
          }));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setDetailLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    }
  };

  const handleAnalyzeSelection = async () => {
    if (!selectedIds.size) {
      window.alert("Select at least one measurement first.");
      return;
    }
    await ensureDetails(Array.from(selectedIds));
  };

  const handleToggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleFilteredSelection = (ids: string[], checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const buildOpticalPropertyRuntimePayload = (
    property: ColorGlassProperty
  ): ColorOpticalPropertyRuntime | null => {
    const defaultModel = defaultGlassPropertyModelMap.get(property.id);
    if (!defaultModel) return null;
    return {
      id: property.id,
      code: property.code,
      name: property.name,
      model_kind: defaultModel.model_kind,
      coefficient_jsonb: defaultModel.coefficient_jsonb
    };
  };

  const handleStartSync = async () => {
    const autoMethod = syncMethodId === AUTO_METHOD_ID;
    const selectedMethod = autoMethod ? null : methods.find((row) => row.id === syncMethodId);
    if (!autoMethod && !selectedMethod) {
      setSyncError("Choose a measurement method before syncing.");
      return;
    }
    if (
      !window.api?.listNasDir ||
      !window.api?.processColorMeasurement ||
      !window.api?.readBase64File ||
      !window.api?.writeTempTextFile
    ) {
      setSyncError("Color sync runtime is not available.");
      return;
    }
    if (!mountPoint) {
      setSyncError("Mount the NAS before syncing.");
      setShowNasLogin(true);
      return;
    }
    const api = window.api;
    if (!api) {
      setSyncError("Color sync runtime is not available.");
      return;
    }

    syncControlRef.current = { pause: false, stop: false };
    setSyncRunning(true);
    setSyncError(null);
    setSyncLogs([]);
    setSyncProgress(buildInitialSyncProgress());

    try {
      const colorRootPath = joinNasPath(mountPoint, NAS_COLOR_INPUT_DIR);
      const [indexRows, rootListing, chemSettingsRows] = await Promise.all([
        fetchColorMeasurementIndex(),
        api.listNasDir(colorRootPath, { withStats: true }),
        fetchChemSettingsFe2O3()
      ]);

      if (!rootListing.ok || !rootListing.entries) {
        throw new Error(rootListing.message || "Failed to list the NAS Color root.");
      }

      const manifestCache = new Map<string, Map<string, ThicknessManifestEntry>>();
      const loadManifest = async (manifestPath: string) => {
        if (manifestCache.has(manifestPath)) {
          return manifestCache.get(manifestPath) as Map<string, ThicknessManifestEntry>;
        }
        const result = await api.readBase64File(manifestPath);
        if (!result.ok || !result.base64) {
          throw new Error(result.message || `Failed to read manifest ${manifestPath}`);
        }
        const parsed = parseThicknessManifestBase64(result.base64, manifestPath);
        manifestCache.set(manifestPath, parsed);
        return parsed;
      };

      const listDirectory = async (dirPath: string) => {
        const listing = await api.listNasDir(dirPath, { withStats: true });
        if (!listing.ok || !listing.entries) {
          throw new Error(listing.message || `Failed to list directory ${dirPath}`);
        }
        return listing.entries;
      };

      const rootThicknessEntry = rootListing.entries.find(
        (entry) => entry.isFile && /^thickness\.xlsx$/i.test(entry.name)
      );
      const rootThicknessManifestPath = rootThicknessEntry
        ? joinNasPath(colorRootPath, rootThicknessEntry.name)
        : null;

      const folderFilter = syncFolderFilter.trim().toLowerCase();
      const allTopFolders = rootListing.entries
        .filter((entry) => entry.isDir)
        .map((entry) => entry.name)
        .sort();
      const topFolders = allTopFolders.filter((name) => {
        if (!folderFilter) return true;
        const lowered = name.toLowerCase();
        // Always traverse References for nested matching.
        return lowered.includes(folderFilter) || lowered === "references";
      });
      const syncScopeFolders = allTopFolders.filter((name) =>
        folderFilter ? name.toLowerCase().includes(folderFilter) : true
      );
      const syncScopePrefixes = syncScopeFolders.map((folder) => `${normalizeCsvRelativePath(folder)}/`);
      const idsToClear = indexRows
        .filter((row) => {
          const path = row.source_csv_path ? normalizeCsvRelativePath(row.source_csv_path) : "";
          return path && syncScopePrefixes.some((prefix) => path.startsWith(prefix));
        })
        .map((row) => row.id);
      const idsToClearSet = new Set(idsToClear);
      const treatAsCleared = syncClearExisting;

      const existingByPath = new Map(
        indexRows
          .filter((row) => !(treatAsCleared && idsToClearSet.has(row.id)))
          .filter((row) => row.source_csv_path)
          .map((row) => [normalizeCsvRelativePath(row.source_csv_path || ""), row])
      );

      const discoverReferenceBatches = async (folderPath: string, folderRelativePath: string) => {
        const discovered: SyncFolderBatch[] = [];
        const stack: Array<{ absolutePath: string; relativePath: string }> = [
          { absolutePath: folderPath, relativePath: folderRelativePath }
        ];

        while (stack.length) {
          const current = stack.pop();
          if (!current) continue;
          let entries: Awaited<ReturnType<typeof listDirectory>>;
          try {
            entries = await listDirectory(current.absolutePath);
          } catch (error) {
            pushSyncLog(
              `Skipping ${current.relativePath}: ${error instanceof Error ? error.message : "folder listing failed"}`,
              "warn"
            );
            continue;
          }

          const thicknessEntry = entries.find(
            (entry) => entry.isFile && entry.name.toLowerCase() === "thickness.csv"
          );
          const csvFiles = entries
            .filter(
              (entry) =>
                entry.isFile &&
                entry.name.toLowerCase().endsWith(".csv") &&
                entry.name.toLowerCase() !== "thickness.csv" &&
                !entry.name.startsWith("~$")
            )
            .map((entry) => entry.name)
            .sort();

          if (thicknessEntry && csvFiles.length) {
            const thicknessManifestPath = joinNasPath(current.absolutePath, thicknessEntry.name);
            const files = csvFiles.map((csvFileName) => {
              const relativePath = normalizeCsvRelativePath(`${current.relativePath}/${csvFileName}`);
              return {
                folderLabel: current.relativePath,
                folderRelativePath: current.relativePath,
                csvFileName,
                inputPath: joinNasPath(current.absolutePath, csvFileName),
                relativePath,
                measurementKey: normalizeMeasurementKeyForThickness(csvFileName),
                measurementLabel: buildMeasurementLabel(relativePath, true),
                thicknessManifestPath,
                isReference: true
              } as SyncFileCandidate;
            });
            discovered.push({
              label: current.relativePath,
              files
            });
          }

          entries
            .filter((entry) => entry.isDir)
            .forEach((entry) => {
              stack.push({
                absolutePath: joinNasPath(current.absolutePath, entry.name),
                relativePath: normalizeCsvRelativePath(`${current.relativePath}/${entry.name}`)
              });
            });
        }

        return discovered.sort((left, right) => left.label.localeCompare(right.label));
      };

      const batches: SyncFolderBatch[] = [];
      for (const folder of topFolders) {
        const folderPath = joinNasPath(colorRootPath, folder);
        const folderRelativePath = normalizeCsvRelativePath(folder);
        if (folder.toLowerCase() === "references") {
          const nestedBatches = await discoverReferenceBatches(folderPath, folderRelativePath);
          if (!nestedBatches.length) {
            pushSyncLog(`No syncable CSV files found under ${folder}.`, "warn");
          }
          batches.push(...nestedBatches);
          continue;
        }

        let entries: Awaited<ReturnType<typeof listDirectory>>;
        try {
          entries = await listDirectory(folderPath);
        } catch (error) {
          pushSyncLog(
            `Skipping ${folder}: ${error instanceof Error ? error.message : "folder listing failed"}`,
            "warn"
          );
          continue;
        }

        const csvFiles = entries
          .filter(
            (entry) =>
              entry.isFile &&
              entry.name.toLowerCase().endsWith(".csv") &&
              entry.name.toLowerCase() !== "thickness.csv" &&
              !entry.name.startsWith("~$")
          )
          .map((entry) => entry.name)
          .sort();
        if (!csvFiles.length) {
          continue;
        }
        if (!rootThicknessManifestPath) {
          pushSyncLog(
            `Skipping ${folder}: missing root thickness.xlsx in ${NAS_COLOR_INPUT_DIR}`,
            "warn"
          );
          continue;
        }

        const files = csvFiles.map((csvFileName) => {
          const relativePath = normalizeCsvRelativePath(`${folderRelativePath}/${csvFileName}`);
          return {
            folderLabel: folder,
            folderRelativePath,
            csvFileName,
            inputPath: joinNasPath(folderPath, csvFileName),
            relativePath,
            measurementKey: normalizeMeasurementKeyForThickness(csvFileName),
            measurementLabel: buildMeasurementLabel(relativePath, false),
            thicknessManifestPath: rootThicknessManifestPath,
            isReference: false
          } as SyncFileCandidate;
        });
        batches.push({ label: folder, files });
      }

      if (!batches.length) {
        pushSyncLog("No CSV files matched the current sync scope.", "warn");
        return;
      }

      const existingIdsByLabel = new Map<string, Set<string>>();
      indexRows
        .filter((row) => !(treatAsCleared && idsToClearSet.has(row.id)))
        .forEach((row) => {
          const key = normalizeMeasurementLabelKey(row.measurement_label || "");
          if (!key) return;
          const set = existingIdsByLabel.get(key) || new Set<string>();
          set.add(row.id);
          existingIdsByLabel.set(key, set);
        });

      const firstPathByLabel = new Map<string, string>();
      const validatedBatches: Array<{ label: string; files: ValidatedSyncFile[] }> = [];
      let skippedByValidation = 0;

      for (const batch of batches) {
        const validFiles: ValidatedSyncFile[] = [];
        for (const file of batch.files) {
          const existingForPath = existingByPath.get(file.relativePath);
          const labelKey = normalizeMeasurementLabelKey(file.measurementLabel);
          if (!labelKey) {
            skippedByValidation += 1;
            pushSyncLog(`Skipping ${file.relativePath}: empty measurement label`, "warn");
            continue;
          }
          const firstPath = firstPathByLabel.get(labelKey);
          if (firstPath && firstPath !== file.relativePath) {
            skippedByValidation += 1;
            pushSyncLog(
              `Skipping ${file.relativePath}: duplicate measurement label "${file.measurementLabel}" in sync scope`,
              "warn"
            );
            continue;
          }
          firstPathByLabel.set(labelKey, file.relativePath);

          const idsForLabel = existingIdsByLabel.get(labelKey);
          if (idsForLabel) {
            const hasConflict = Array.from(idsForLabel).some((id) => id !== existingForPath?.id);
            if (hasConflict) {
              skippedByValidation += 1;
              pushSyncLog(
                `Skipping ${file.relativePath}: measurement label "${file.measurementLabel}" already exists`,
                "warn"
              );
              continue;
            }
          }

          let manifestMap: Map<string, ThicknessManifestEntry>;
          try {
            manifestMap = await loadManifest(file.thicknessManifestPath);
          } catch (error) {
            skippedByValidation += 1;
            pushSyncLog(
              `Skipping ${file.relativePath}: ${error instanceof Error ? error.message : "manifest parse failed"}`,
              "warn"
            );
            continue;
          }

          const manifestEntry = manifestMap.get(file.measurementKey);
          if (!manifestEntry) {
            skippedByValidation += 1;
            pushSyncLog(
              `Skipping ${file.relativePath}: missing thickness entry for "${file.measurementKey}"`,
              "warn"
            );
            continue;
          }
          if (!(manifestEntry.thicknessMeanMm && manifestEntry.thicknessMeanMm > 0)) {
            skippedByValidation += 1;
            pushSyncLog(
              `Skipping ${file.relativePath}: invalid thickness mean for "${file.measurementKey}"`,
              "warn"
            );
            continue;
          }

          validFiles.push({
            ...file,
            manifestEntry
          });
        }

        if (validFiles.length) {
          validatedBatches.push({
            label: batch.label,
            files: validFiles
          });
        }
      }

      const totalFiles = validatedBatches.reduce((sum, batch) => sum + batch.files.length, 0);
      setSyncProgress((prev) => ({
        ...prev,
        foldersTotal: validatedBatches.length,
        totalFiles
      }));

      if (!totalFiles) {
        pushSyncLog("Validation excluded all files from this sync run.", "warn");
        return;
      }
      if (skippedByValidation) {
        pushSyncLog(`Pre-sync validation skipped ${skippedByValidation} file(s).`, "warn");
      }

      if (syncClearExisting) {
        const scopeLabel = syncFolderFilter.trim() ? "filtered sync scope" : "discovered sync scope";
        if (syncDryRun) {
          pushSyncLog(
            idsToClear.length
              ? `Would clear ${idsToClear.length} existing measurements in the ${scopeLabel} before re-sync.`
              : `No existing measurements would be cleared in the ${scopeLabel}.`,
            "warn"
          );
        } else if (idsToClear.length) {
          const confirmed = window.confirm(
            `Clear ${idsToClear.length} existing Color measurements in the ${scopeLabel} before re-sync? This cannot be undone.`
          );
          if (!confirmed) {
            pushSyncLog("Clear-before-sync cancelled by user.", "warn");
            return;
          }
          pushSyncLog(`Clearing ${idsToClear.length} existing measurements before re-sync...`, "warn");
          await deleteColorMeasurements(idsToClear);
          pushSyncLog("Existing measurements cleared.", "success");
        } else {
          pushSyncLog("No existing measurements matched the current sync scope.", "info");
        }
      }

      const existingLabelById = new Map<string, string>();
      const existingIdsByLabelMutable = new Map<string, Set<string>>();
      indexRows
        .filter((row) => !(treatAsCleared && idsToClearSet.has(row.id)))
        .forEach((row) => {
          const key = normalizeMeasurementLabelKey(row.measurement_label || "");
          if (!key) return;
          existingLabelById.set(row.id, key);
          const set = existingIdsByLabelMutable.get(key) || new Set<string>();
          set.add(row.id);
          existingIdsByLabelMutable.set(key, set);
        });

      const setMeasurementLabelForId = (id: string, nextLabel: string) => {
        const nextKey = normalizeMeasurementLabelKey(nextLabel);
        const previousKey = existingLabelById.get(id);
        if (previousKey) {
          const set = existingIdsByLabelMutable.get(previousKey);
          if (set) {
            set.delete(id);
            if (!set.size) existingIdsByLabelMutable.delete(previousKey);
          }
        }
        if (!nextKey) {
          existingLabelById.delete(id);
          return;
        }
        existingLabelById.set(id, nextKey);
        const set = existingIdsByLabelMutable.get(nextKey) || new Set<string>();
        set.add(id);
        existingIdsByLabelMutable.set(nextKey, set);
      };

      let foldersDone = 0;
      let totalFilesDone = 0;

      for (const batch of validatedBatches) {
        while (syncControlRef.current.pause && !syncControlRef.current.stop) {
          await sleep(SYNC_PAUSE_POLL_MS);
        }
        if (syncControlRef.current.stop) break;

        const nonReferenceFiles = batch.files.filter((file) => !file.isReference);
        let workerThicknessManifestPath: string | null = null;
        if (nonReferenceFiles.length) {
          const writeResult = await api.writeTempTextFile({
            prefix: `color_thickness_${batch.label}`,
            extension: "csv",
            text: buildThicknessManifestCsvText(nonReferenceFiles)
          });
          if (!writeResult.ok || !writeResult.filePath) {
            throw new Error(
              writeResult.message ||
                `Failed to create temporary thickness manifest for ${batch.label}`
            );
          }
          workerThicknessManifestPath = writeResult.filePath;
        }

        setSyncProgress((prev) => ({
          ...prev,
          currentFolder: batch.label,
          foldersDone,
          filesTotal: batch.files.length,
          filesDone: 0
        }));
        pushSyncLog(`Scanning ${batch.label} (${batch.files.length} CSV files)`, "info");

        let filesDone = 0;
        for (const candidate of batch.files) {
          while (syncControlRef.current.pause && !syncControlRef.current.stop) {
            await sleep(SYNC_PAUSE_POLL_MS);
          }
          if (syncControlRef.current.stop) break;

          const measurementTimestamp = candidate.isReference
            ? null
            : parseMeasurementTimestamp(candidate.csvFileName);
          const nearestChemSetting = candidate.isReference
            ? null
            : findNearestChemSetting(chemSettingsRows, measurementTimestamp);
          const xrfFe2O3 =
            nearestChemSetting && Number.isFinite(Number(nearestChemSetting.Fe2O3))
              ? Number(nearestChemSetting.Fe2O3)
              : null;
          const inputFeTotalPercent = candidate.isReference
            ? candidate.manifestEntry.inputFeTotalPercent ?? undefined
            : undefined;
          const inputFe2O3Percent = candidate.isReference
            ? candidate.manifestEntry.inputFe2O3Percent ?? undefined
            : xrfFe2O3 ?? candidate.manifestEntry.inputFe2O3Percent ?? undefined;

          const relativePath = candidate.relativePath;
          const opticalPropertyCode = resolveOpticalPropertyCode(relativePath);
          const glassProperty = glassPropertyCodeMap.get(opticalPropertyCode);
          const opticalProperty = glassProperty
            ? buildOpticalPropertyRuntimePayload(glassProperty)
            : null;
          const derivedFeTotalPercent = resolveFeTotalPercent(
            inputFeTotalPercent,
            inputFe2O3Percent
          );
          pushSyncLog(`Processing ${relativePath}`, "info");
          if (!measurementTimestamp && !candidate.isReference) {
            pushSyncLog(
              `No filename timestamp for ${relativePath}; skipping chem_settings iron lookup`,
              "warn"
            );
          }

          const colorResolution = resolveGlassColorForMeasurement({
            isReference: candidate.isReference,
            measuredAt: measurementTimestamp,
            trialDurations: trialDurationRows,
            mappingByTrialId: colorTrialMappingByTrialId
          });
          const {
            activeGlaschemieTrial,
            mappedGlassColor,
            glassColor,
            usedReferenceFallback,
            glassColorTrialDurationId
          } = colorResolution;
          const method = selectedMethod || resolveAutoMeasurementMethod(methods, glassColor);
          if (!method) {
            const reason = candidate.isReference
              ? `no active measurement method matches reference fallback "${glassColor || REFERENCE_FALLBACK_METHOD_NAME}"`
              : activeGlaschemieTrial
                ? mappedGlassColor
                  ? `no active measurement method matches mapped color "${mappedGlassColor}"`
                  : `no Color mapping for Glaschemie trial "${activeGlaschemieTrial.descriptive_name}"`
                : "no active Glaschemie trial matched the measurement timestamp";
            pushSyncLog(`Skipping ${relativePath}: Auto measurement method failed (${reason}).`, "warn");
            continue;
          }
          if (autoMethod) {
            pushSyncLog(
              candidate.isReference && usedReferenceFallback
                ? `Auto method: ${method.name} from ${glassColor} (References fallback)`
                : glassColor
                  ? `Auto method: ${method.name} from ${glassColor}${
                      activeGlaschemieTrial ? ` (${activeGlaschemieTrial.descriptive_name})` : ""
                    }`
                  : `Auto method: ${method.name}`,
              "info"
            );
          }

          try {
            const response = parseColorProcessResponse(
              await api.processColorMeasurement(
                buildColorProcessRequest({
                  inputPath: candidate.inputPath,
                  method,
                  opticalProfile: opticalProperty || undefined,
                  relativePath,
                  measurementName: candidate.measurementKey,
                  measurementLabel: candidate.measurementLabel,
                  thicknessCsvPath: candidate.isReference
                    ? candidate.thicknessManifestPath
                    : workerThicknessManifestPath || undefined,
                  inputFe2O3Percent,
                  inputFeTotalPercent
                })
              )
            );
            if (!isColorProcessSuccess(response)) {
              throw new Error(
                formatWorkerProcessingError(
                  response.error.code,
                  response.error.message,
                  response.error.details || {}
                )
              );
            }
            response.warnings.forEach((warning) => {
              pushSyncLog(`${relativePath}: ${warning.message}`, "warn");
            });
            const result = response;
            const existing = existingByPath.get(relativePath);
            const incomingLabel = String(result.measurement.measurement_label || candidate.measurementLabel || "").trim();
            const incomingLabelKey = normalizeMeasurementLabelKey(incomingLabel);
            if (!incomingLabelKey) {
              pushSyncLog(`Skipped ${relativePath}: worker returned an empty measurement label`, "warn");
              continue;
            }
            const conflictingIds = existingIdsByLabelMutable.get(incomingLabelKey);
            if (
              conflictingIds &&
              Array.from(conflictingIds).some((rowId) => rowId !== existing?.id)
            ) {
              pushSyncLog(
                `Skipped ${relativePath}: measurement label "${incomingLabel}" conflicts with an existing record`,
                "warn"
              );
              continue;
            }
            if (
              !candidate.isReference &&
              activeGlaschemieTrial &&
              !mappedGlassColor
            ) {
              pushSyncLog(
                `No Color mapping for Glaschemie trial "${activeGlaschemieTrial.descriptive_name}" (${relativePath}); color left empty.`,
                "warn"
              );
            }
            if (
              existing?.source_file_checksum &&
              existing.source_file_checksum === result.measurement.source_file_checksum
            ) {
              const existingGlassColor = existing.glass_color?.trim() || null;
              const existingTrialId =
                existing.glass_color_trial_duration_id == null
                  ? null
                  : Number(existing.glass_color_trial_duration_id);
              const colorNeedsUpdate =
                existingGlassColor !== glassColor ||
                (Number.isFinite(existingTrialId) ? existingTrialId : null) !==
                  glassColorTrialDurationId;
              if (colorNeedsUpdate) {
                if (syncDryRun) {
                  pushSyncLog(
                    `Would update color mapping for unchanged ${relativePath}${
                      glassColor ? ` (${glassColor})` : " (empty)"
                    }`,
                    "success"
                  );
                } else {
                  await updateColorMeasurement(existing.id, {
                    glass_color: glassColor,
                    glass_color_trial_duration_id: glassColorTrialDurationId
                  });
                  existingByPath.set(relativePath, {
                    ...existing,
                    glass_color: glassColor,
                    glass_color_trial_duration_id: glassColorTrialDurationId
                  });
                  pushSyncLog(
                    `Updated color mapping for unchanged ${relativePath}${
                      glassColor ? ` (${glassColor})` : " (empty)"
                    }`,
                    "success"
                  );
                }
              } else {
                pushSyncLog(`Unchanged: ${relativePath}`, "success");
              }
            } else {
              if (!glassProperty) {
                pushSyncLog(
                  `Skipped ${relativePath}: no seeded optical property for code ${opticalPropertyCode}`,
                  "warn"
                );
              } else if (!opticalProperty) {
                pushSyncLog(
                  `Skipped ${relativePath}: optical property ${glassProperty.name} has no default model`,
                  "warn"
                );
              } else {
                const payload: ColorMeasurementPayload = {
                  measurement_method_id: method.id,
                  glass_property_id: glassProperty.id,
                  reference_measurement_id: null,
                  ...result.measurement,
                  glass_color: glassColor,
                  glass_color_trial_duration_id: glassColorTrialDurationId,
                  applied_transmittance_mode_id: method.transmittance_mode_id,
                  applied_reflection_correction_model_id: method.reflection_correction_model_id,
                  applied_observer_id: method.observer_id,
                  applied_illuminant_id: method.illuminant_id
                };

                if (syncDryRun) {
                  const chemistrySource = candidate.isReference
                    ? candidate.manifestEntry.inputFeTotalPercent != null
                      ? "reference thickness manifest (fe_total)"
                      : candidate.manifestEntry.inputFe2O3Percent != null
                        ? "reference thickness manifest (fe2o3)"
                        : "no reference manifest chemistry"
                    : xrfFe2O3 != null
                      ? "chem_settings"
                      : candidate.manifestEntry.inputFe2O3Percent != null
                        ? "thickness manifest fallback"
                        : "no chemistry source";
                  const chemistryInfo =
                    derivedFeTotalPercent == null
                      ? `without FeTotal (${chemistrySource})`
                      : `with FeTotal=${derivedFeTotalPercent.toFixed(4)} (${chemistrySource})`;
                  const colorInfo = glassColor
                    ? `, color=${glassColor}${
                        activeGlaschemieTrial
                          ? ` (Glaschemie: ${activeGlaschemieTrial.descriptive_name})`
                          : ""
                      }`
                    : "";
                  pushSyncLog(
                    `${existing ? "Would update" : "Would create"} ${relativePath} using ${glassProperty.name} ${chemistryInfo}${colorInfo}`,
                    "success"
                  );
                } else {
                  const measurementId = existing
                    ? (await updateColorMeasurement(existing.id, payload)).id
                    : (await createColorMeasurement(payload)).id;
                  await replaceColorMeasurementCurves(measurementId, result.curves);
                  setMeasurementLabelForId(measurementId, payload.measurement_label || "");
                  existingByPath.set(relativePath, {
                    id: measurementId,
                    measurement_label: payload.measurement_label || null,
                    measured_at: payload.measured_at,
                    source_csv_path: relativePath,
                    source_file_checksum: payload.source_file_checksum,
                    glass_color: glassColor,
                    glass_color_trial_duration_id: glassColorTrialDurationId
                  });
                  pushSyncLog(`${existing ? "Updated" : "Created"} ${relativePath}`, "success");
                }
              }
            }
          } catch (err) {
            pushSyncLog(
              `Failed ${relativePath}: ${err instanceof Error ? err.message : "Unknown error"}`,
              "error"
            );
          } finally {
            filesDone += 1;
            totalFilesDone += 1;
            setSyncProgress((prev) => ({
              ...prev,
              currentFolder: batch.label,
              filesDone,
              totalFilesDone
            }));
          }
        }

        foldersDone += 1;
        setSyncProgress((prev) => ({
          ...prev,
          foldersDone,
          currentFolder: batch.label,
          filesDone: prev.filesTotal
        }));
      }

      if (syncControlRef.current.stop) {
        pushSyncLog("Sync stopped by user.", "warn");
      } else {
        pushSyncLog(syncDryRun ? "Dry run completed." : "Sync completed.", "success");
      }
      if (!syncDryRun) {
        clearCache();
        setDetailCache({});
        await loadAllData();
      }
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : "Color sync failed");
    } finally {
      setSyncRunning(false);
      setSyncPaused(false);
      setSyncStopRequested(false);
      syncControlRef.current = { pause: false, stop: false };
    }
  };

  const handleExportMissingThicknessRows = async () => {
    if (
      !window.api?.listNasDir ||
      !window.api?.readBase64File
    ) {
      setSyncError("Color sync runtime is not available.");
      return;
    }
    if (!mountPoint) {
      setSyncError("Mount the NAS before scanning thickness rows.");
      setShowNasLogin(true);
      return;
    }
    const api = window.api;
    if (!api) {
      setSyncError("Color sync runtime is not available.");
      return;
    }

    setSyncError(null);
    setSyncMissingRowsExporting(true);
    pushSyncLog("Scanning NAS for missing thickness.xlsx rows...", "info");

    try {
      const colorRootPath = joinNasPath(mountPoint, NAS_COLOR_INPUT_DIR);
      const rootListing = await api.listNasDir(colorRootPath, { withStats: true });
      if (!rootListing.ok || !rootListing.entries) {
        throw new Error(rootListing.message || "Failed to list the NAS Color root.");
      }

      const rootThicknessEntry = rootListing.entries.find(
        (entry) => entry.isFile && /^thickness\.xlsx$/i.test(entry.name)
      );
      if (!rootThicknessEntry) {
        pushSyncLog(`Missing root thickness.xlsx in ${NAS_COLOR_INPUT_DIR}.`, "warn");
        window.alert(`Missing root thickness.xlsx in ${NAS_COLOR_INPUT_DIR}.`);
        return;
      }

      const rootThicknessManifestPath = joinNasPath(colorRootPath, rootThicknessEntry.name);
      const manifestResult = await api.readBase64File(rootThicknessManifestPath);
      if (!manifestResult.ok || !manifestResult.base64) {
        throw new Error(
          manifestResult.message || `Failed to read manifest ${rootThicknessManifestPath}`
        );
      }
      const manifestMap = parseThicknessManifestBase64(
        manifestResult.base64,
        rootThicknessManifestPath
      );

      const folderFilter = syncFolderFilter.trim().toLowerCase();
      const topFolders = rootListing.entries
        .filter((entry) => entry.isDir)
        .map((entry) => entry.name)
        .filter((name) => name.toLowerCase() !== "references")
        .filter((name) => (folderFilter ? name.toLowerCase().includes(folderFilter) : true))
        .sort();

      let scannedCsvCount = 0;
      const missingRows: MissingThicknessManifestRow[] = [];

      for (const folder of topFolders) {
        const folderPath = joinNasPath(colorRootPath, folder);
        const listing = await api.listNasDir(folderPath, { withStats: true });
        if (!listing.ok || !listing.entries) {
          pushSyncLog(
            `Skipping ${folder}: ${listing.message || "folder listing failed"}`,
            "warn"
          );
          continue;
        }
        const csvFiles = listing.entries
          .filter(
            (entry) =>
              entry.isFile &&
              entry.name.toLowerCase().endsWith(".csv") &&
              entry.name.toLowerCase() !== "thickness.csv" &&
              !entry.name.startsWith("~$")
          )
          .map((entry) => entry.name)
          .sort();

        for (const csvFileName of csvFiles) {
          scannedCsvCount += 1;
          const sampleKey = normalizeMeasurementKeyForThickness(csvFileName);
          if (!sampleKey || manifestMap.has(sampleKey)) continue;
          const relativePath = normalizeCsvRelativePath(`${folder}/${csvFileName}`);
          missingRows.push({
            folder,
            relativePath,
            csvFileName,
            sampleKey,
            manifestPath: rootThicknessManifestPath
          });
        }
      }

      if (!missingRows.length) {
        pushSyncLog(
          `No missing thickness.xlsx rows found in ${scannedCsvCount} CSV file(s).`,
          "success"
        );
        window.alert("No missing thickness.xlsx rows found in the current sync scope.");
        return;
      }

      const csv = buildMissingThicknessRowsCsvText(missingRows);
      downloadText(
        csv,
        buildExportFileName("color_missing_thickness_rows", "csv"),
        "text/csv"
      );
      pushSyncLog(
        `Exported ${missingRows.length} missing thickness.xlsx row(s) from ${scannedCsvCount} CSV file(s).`,
        "success"
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Missing-row export failed.";
      setSyncError(message);
      pushSyncLog(`Missing-row export failed: ${message}`, "error");
    } finally {
      setSyncMissingRowsExporting(false);
    }
  };

  const handleExportReportHtml = async () => {
    if (!reportBasis.length) {
      window.alert("No Color measurements are available for export.");
      return;
    }
    await ensureDetails(reportBasis.map((row) => row.id));
    const viewingConditions = summarizeViewingConditions(reportBasis);

    const mean = (values: number[]) =>
      values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
    const brightnessMean = mean(
      reportBasis
        .map((row) => row.brightness_percent)
        .filter((value): value is number => Number.isFinite(value))
    );
    const fe2FeTotalMean = mean(
      reportBasis
        .map((row) => row.fe2_fe_total_percent)
        .filter((value): value is number => Number.isFinite(value))
    );
    const fe2Fe3Mean = mean(
      reportBasis
        .map((row) => row.fe2_fe3_ratio)
        .filter((value): value is number => Number.isFinite(value))
    );
    const wavelengthMean = mean(
      reportBasis
        .map((row) => row.dominant_wavelength_nm)
        .filter((value): value is number => Number.isFinite(value))
    );

    const spectraSvg = renderLineChartSvg(
      `Color Spectra (${COLOR_CURVE_LABELS[curveKind]})`,
      reportBasis
        .map((row, index) => {
          const detail = detailCache[row.id];
          const points = getCurvePoints(detail, curveKind);
          return {
            label: formatReferenceLabel(row),
            color: colorForIndex(index),
            points: points.map((point) => ({
              x: point.wavelength_nm,
              y: point.value
            }))
          };
        })
        .filter((row) => row.points.length)
    );

    const chromaticitySvg = renderChromaticitySvg(
      `Chromaticity Overview (${viewingConditions.label})`,
      reportBasis
        .filter((row) => Number.isFinite(row.x_chromaticity) && Number.isFinite(row.y_chromaticity))
        .map((row, index) => ({
          id: row.id,
          label: formatReferenceLabel(row),
          color: colorForIndex(index),
          x: row.x_chromaticity as number,
          y: row.y_chromaticity as number
        })),
      null
    );

    const labSvg = renderLabSvg(
      `L*a*b* Color Space (${viewingConditions.label})`,
      reportBasis
        .filter((row) => Number.isFinite(row.l_star) && Number.isFinite(row.a_star) && Number.isFinite(row.b_star))
        .map((row, index) => ({
          id: row.id,
          label: formatReferenceLabel(row),
          l: row.l_star as number,
          a: row.a_star as number,
          b: row.b_star as number,
          color: colorForIndex(index)
        })),
      null
    );

    const trialSummary = Object.entries(effectiveFilters.trials)
      .filter(([, values]) => values.length)
      .map(([trialType, values]) => `${trialType}: ${values.join(", ")}`);

    const filterRows = [
      ["Date", formatDateFilterDisplay(effectiveFilters.date)],
      [
        "Methods",
        effectiveFilters.methodIds.length
          ? effectiveFilters.methodIds
              .map((id) => methodMap.get(id)?.name || id)
              .join(", ")
          : "All"
      ],
      [
        "Observers",
        effectiveFilters.observerIds.length
          ? effectiveFilters.observerIds
              .map((id) => observerMap.get(id)?.code || id)
              .join(", ")
          : viewingConditions.observers.length
            ? viewingConditions.observers.join(", ")
            : "All"
      ],
      [
        "Illuminants",
        effectiveFilters.illuminantIds.length
          ? effectiveFilters.illuminantIds
              .map((id) => illuminantMap.get(id)?.code || id)
              .join(", ")
          : viewingConditions.illuminants.length
            ? viewingConditions.illuminants.join(", ")
            : "All"
      ],
      [
        "Optical Properties",
        effectiveFilters.glassPropertyIds.length
          ? effectiveFilters.glassPropertyIds
              .map((id) => glassPropertyMap.get(id)?.name || id)
              .join(", ")
          : "All"
      ],
      [
        "Color",
        effectiveFilters.glassColors.length ? effectiveFilters.glassColors.join(", ") : "All"
      ],
      ["Trials", trialSummary.length ? trialSummary.join("; ") : "All"],
      ["Search", searchTerm || "All"],
      ["Curve", COLOR_CURVE_LABELS[curveKind]]
    ];

    const measurementRegisterHeaders = [
      "Measured At",
      "Measurement",
      "Method",
      "Color",
      "Brightness",
      "Fe2 / Fe Total",
      "Fe2 / Fe3",
      "Wavelength",
      "Observer",
      "Illuminant"
    ];
    const measurementRowsForHtml = reportBasis.map((row) => [
      formatAppDateTime(row.measured_at),
      formatReferenceLabel(row),
      row.method_name || "—",
      row.glass_color || "—",
      formatPercent(row.brightness_percent, 2),
      formatPercent(row.fe2_fe_total_percent, 2),
      formatMetric(row.fe2_fe3_ratio, 3),
      formatWavelength(row.dominant_wavelength_nm, row.wavelength_mode),
      resolveMeasurementObserver(row) || "—",
      resolveMeasurementIlluminant(row) || "—"
    ]);
    let redoxAbsorbanceById: Record<string, number | null> = {};
    let redoxTrendWarning = "";
    try {
      redoxAbsorbanceById = await fetchColorAbsorbancePerMmAtWavelength(
        reportBasis.map((row) => row.id),
        REDOX_TIME_OVERLAY_ABSORBANCE_WAVELENGTH_NM
      );
    } catch (error) {
      redoxTrendWarning = `Abs/mm@${REDOX_TIME_OVERLAY_ABSORBANCE_WAVELENGTH_NM} values could not be loaded: ${
        error instanceof Error ? error.message : "unknown error"
      }`;
    }
    const redoxTrendPlot = buildRedoxTimeOverlayPlot(reportBasis, redoxAbsorbanceById);
    const redoxTrendLayout = {
      ...redoxTrendPlot.layout,
      title: REDOX_TIME_OVERLAY_LABEL,
      margin: {
        l: 64,
        r: 58,
        t: 64,
        b: 72
      }
    };
    const redoxTrendSeriesRows = (redoxTrendPlot.data as Array<{ name?: unknown; x?: unknown[]; y?: unknown[] }>)
      .map((trace, index) => {
        const count = Array.isArray(trace.x)
          ? trace.x.length
          : Array.isArray(trace.y)
            ? trace.y.length
            : 0;
        return [
          typeof trace.name === "string" && trace.name.trim()
            ? trace.name
            : `Series ${index + 1}`,
          String(count)
        ];
      })
      .filter((row) => row[1] !== "0");
    const redoxTrendPlotDataJson = serializeForInlineScript(redoxTrendPlot.data);
    const redoxTrendPlotLayoutJson = serializeForInlineScript(redoxTrendLayout);
    const redoxTrendPlotConfigJson = serializeForInlineScript({
      responsive: true,
      displaylogo: false
    });
    const measurementRegisterHeadersJson = serializeForInlineScript(measurementRegisterHeaders);
    const measurementRegisterRowsJson = serializeForInlineScript(measurementRowsForHtml);

    const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Color Measurement Report</title>
    <style>
      :root {
        --bg: #f2f5fb;
        --surface: #ffffff;
        --ink: #142032;
        --muted: #5f6f85;
        --line: #d7dfeb;
        --accent: #144a88;
        --accent-soft: #e8f1ff;
        --warning-bg: #fff7df;
        --warning-line: #e2bb4b;
      }
      body { font-family: ${PLOT_FONT_FAMILY}; margin: 0; color: var(--ink); background: radial-gradient(circle at top right, #dce9ff 0%, #f5f8fd 55%, #edf2f9 100%); }
      .report-shell { max-width: 1320px; margin: 0 auto; padding: 26px; }
      .hero { background: linear-gradient(132deg, #0f2748 0%, #1f4d86 48%, #0f7f76 100%); color: #ffffff; border-radius: 22px; padding: 24px 28px; box-shadow: 0 24px 48px rgba(15,39,72,0.22); }
      .hero h1 { margin: 0; font-size: 30px; letter-spacing: 0.01em; }
      .hero-meta { margin-top: 7px; color: rgba(255,255,255,0.86); font-size: 13px; }
      .hero-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-top: 18px; }
      .hero-card { background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.24); border-radius: 14px; padding: 12px 14px; }
      .hero-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.09em; opacity: 0.82; }
      .hero-value { font-size: 24px; font-weight: 700; margin-top: 4px; }
      .hero-note { font-size: 12px; opacity: 0.82; margin-top: 4px; }
      .section { margin-top: 18px; background: var(--surface); border: 1px solid var(--line); border-radius: 18px; padding: 18px; box-shadow: 0 16px 30px rgba(15,23,42,0.06); }
      .section h2 { margin: 0 0 10px; font-size: 19px; }
      .warning { background: var(--warning-bg); border: 1px solid var(--warning-line); border-radius: 12px; color: #704f00; padding: 10px 12px; font-size: 13px; margin-bottom: 12px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid var(--line); padding: 8px 10px; font-size: 12px; text-align: left; vertical-align: top; }
      th { background: #f1f6ff; color: #17396f; }
      .kpi-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
      .kpi-card { border: 1px solid var(--line); border-radius: 12px; padding: 12px; background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%); }
      .kpi-title { font-size: 11px; color: var(--muted); text-transform: uppercase; letter-spacing: 0.08em; }
      .kpi-value { font-size: 20px; font-weight: 700; margin-top: 4px; color: var(--accent); }
      .kpi-foot { font-size: 12px; margin-top: 2px; color: var(--muted); }
      .chart-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
      .chart-span-2 { grid-column: 1 / -1; }
      .svg-block { border: 1px solid var(--line); border-radius: 14px; padding: 8px; background: linear-gradient(180deg, #ffffff 0%, #f4f7fc 100%); }
      .section-note { margin: 0 0 12px; color: var(--muted); font-size: 12px; }
      #report-redox-plot-wrap { border-radius: 14px; overflow: hidden; border: 1px solid rgba(20,74,136,0.24); background: #0f1e35; }
      #report-redox-status { margin-top: 10px; font-size: 12px; color: #9f1239; }
      .table-scroll { overflow-x: auto; border: 1px solid var(--line); border-radius: 12px; }
      .table-scroll table { border: 0; }
      .table-scroll th:first-child, .table-scroll td:first-child { border-left: 0; }
      .table-scroll th:last-child, .table-scroll td:last-child { border-right: 0; }
      .table-toolbar, .pagination-controls { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin: 10px 0; }
      .pagination-controls { margin-bottom: 0; }
      .pagination-buttons { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
      .pagination-button { border: 1px solid var(--line); border-radius: 9px; background: #f8fbff; color: var(--accent); padding: 7px 11px; font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; }
      .pagination-button:disabled { opacity: 0.46; cursor: not-allowed; }
      .page-size-select { border: 1px solid var(--line); border-radius: 9px; background: #ffffff; color: var(--ink); padding: 7px 9px; font: inherit; font-size: 12px; }
      .pagination-summary { color: var(--muted); font-size: 12px; }
      @media (max-width: 980px) {
        .hero-grid, .kpi-grid, .chart-grid { grid-template-columns: 1fr; }
      }
    </style>
  </head>
  <body>
    <div class="report-shell">
      <div class="hero">
        <h1>Color Measurement Executive Report</h1>
        <p class="hero-meta">Exported at ${escapeHtml(
          formatCurrentDateTime()
        )} | Basis: ${escapeHtml(selectedMeasurements.length ? "Selected rows" : "Filtered rows")}</p>
        <div class="hero-grid">
          <div class="hero-card">
            <div class="hero-label">Measurements</div>
            <div class="hero-value">${reportBasis.length}</div>
            <div class="hero-note">Rows in analytical scope</div>
          </div>
          <div class="hero-card">
            <div class="hero-label">Hydrated Curves</div>
            <div class="hero-value">${reportDetailBasis.length}</div>
            <div class="hero-note">Curves loaded for chart export</div>
          </div>
          <div class="hero-card">
            <div class="hero-label">Viewing Conditions</div>
            <div class="hero-value">${escapeHtml(viewingConditions.label)}</div>
            <div class="hero-note">${escapeHtml(COLOR_CURVE_LABELS[curveKind])}</div>
          </div>
          <div class="hero-card">
            <div class="hero-label">Mean Brightness</div>
            <div class="hero-value">${formatPercent(brightnessMean, 2)}</div>
            <div class="hero-note">Relative Y/Yn-based brightness</div>
          </div>
        </div>
      </div>
      <div class="section">
        <h2>Scope & Filters</h2>
        ${viewingConditions.warning ? `<div class="warning">${escapeHtml(viewingConditions.warning)}</div>` : ""}
        ${renderHtmlTable(["Filter", "Value"], filterRows)}
      </div>
      <div class="section">
        <h2>Key Indicators</h2>
        <div class="kpi-grid">
          <div class="kpi-card">
            <div class="kpi-title">Brightness Mean</div>
            <div class="kpi-value">${formatPercent(brightnessMean, 2)}</div>
            <div class="kpi-foot">Computed from CIE Y / Yn</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Fe2 / Fe Total Mean</div>
            <div class="kpi-value">${formatPercent(fe2FeTotalMean, 2)}</div>
            <div class="kpi-foot">Project redox proxy</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Fe2 / Fe3 Mean Ratio</div>
            <div class="kpi-value">${formatMetric(fe2Fe3Mean, 3)}</div>
            <div class="kpi-foot">Higher values indicate stronger Fe2 contribution</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Dominant Wavelength Mean</div>
            <div class="kpi-value">${formatMetric(wavelengthMean, 1)}</div>
            <div class="kpi-foot">nm</div>
          </div>
        </div>
      </div>
      <div class="section">
        <h2>Interactive Redox, XRF, and Absorbance Trend</h2>
        <p class="section-note">Embedded from the Trend HTML export for ${escapeHtml(REDOX_TIME_OVERLAY_LABEL)}.</p>
        ${redoxTrendWarning ? `<div class="warning">${escapeHtml(redoxTrendWarning)}</div>` : ""}
        ${
          redoxTrendPlot.hasData
            ? `<div id="report-redox-plot-wrap">
          <div id="report-redox-trend-plot" style="width:100%;height:680px;"></div>
        </div>
        <div id="report-redox-status"></div>
        <h3>Series Coverage</h3>
        ${renderHtmlTable(
          ["Series", "Points"],
          redoxTrendSeriesRows.length ? redoxTrendSeriesRows : [["No series", "0"]]
        )}`
            : `<div class="warning">No Fe2/FeTotal, Fe2O3 input, Fe2, Fe total, or A${REDOX_TIME_OVERLAY_ABSORBANCE_WAVELENGTH_NM} Abs/mm values are available for this report scope.</div>`
        }
      </div>
      <div class="section">
        <h2>Measurement Register</h2>
        <div class="table-toolbar">
          <div class="pagination-summary" id="measurement-register-count">Rows: ${measurementRowsForHtml.length}</div>
          <label class="pagination-summary">
            Rows per page
            <select class="page-size-select" id="measurement-register-page-size">
              <option value="12">12</option>
              <option value="25" selected>25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </label>
        </div>
        <div class="table-scroll">
          <table id="measurement-register-table">
            <thead id="measurement-register-head"></thead>
            <tbody id="measurement-register-body"></tbody>
          </table>
        </div>
        <div class="pagination-controls">
          <div class="pagination-summary" id="measurement-register-page-label"></div>
          <div class="pagination-buttons">
            <button class="pagination-button" type="button" id="measurement-register-prev">Prev</button>
            <button class="pagination-button" type="button" id="measurement-register-next">Next</button>
          </div>
        </div>
        <noscript>
          ${renderHtmlTable(measurementRegisterHeaders, measurementRowsForHtml)}
        </noscript>
      </div>
      <div class="section">
        <h2>Visual Analysis & Diagnostics</h2>
        <div class="chart-grid">
          <div class="svg-block chart-span-2">${spectraSvg}</div>
          <div class="svg-block">${chromaticitySvg}</div>
          <div class="svg-block">${labSvg}</div>
        </div>
      </div>
    </div>
    <script>
      (() => {
        const headers = ${measurementRegisterHeadersJson};
        const rows = ${measurementRegisterRowsJson};
        const state = { page: 0, pageSize: 25 };
        const head = document.getElementById("measurement-register-head");
        const body = document.getElementById("measurement-register-body");
        const count = document.getElementById("measurement-register-count");
        const label = document.getElementById("measurement-register-page-label");
        const prev = document.getElementById("measurement-register-prev");
        const next = document.getElementById("measurement-register-next");
        const pageSize = document.getElementById("measurement-register-page-size");
        const escapeCell = (value) =>
          String(value ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
        const totalPages = () => Math.max(1, Math.ceil(rows.length / state.pageSize));
        const render = () => {
          if (!head || !body || !label || !prev || !next || !pageSize || !count) return;
          state.page = Math.max(0, Math.min(state.page, totalPages() - 1));
          const start = state.page * state.pageSize;
          const pageRows = rows.slice(start, start + state.pageSize);
          head.innerHTML = "<tr>" + headers.map((header) => "<th>" + escapeCell(header) + "</th>").join("") + "</tr>";
          body.innerHTML = pageRows.length
            ? pageRows
                .map((row) => "<tr>" + headers.map((_, index) => "<td>" + escapeCell(row[index]) + "</td>").join("") + "</tr>")
                .join("")
            : '<tr><td colspan="' + headers.length + '">No measurements in this report scope.</td></tr>';
          const shownStart = rows.length ? start + 1 : 0;
          const shownEnd = Math.min(start + pageRows.length, rows.length);
          count.textContent = "Rows: " + rows.length;
          label.textContent = "Showing " + shownStart + "-" + shownEnd + " of " + rows.length + " | Page " + (state.page + 1) + " / " + totalPages();
          prev.disabled = state.page <= 0;
          next.disabled = state.page >= totalPages() - 1;
        };
        pageSize?.addEventListener("change", (event) => {
          state.pageSize = Number(event.target.value) || 25;
          state.page = 0;
          render();
        });
        prev?.addEventListener("click", () => {
          state.page -= 1;
          render();
        });
        next?.addEventListener("click", () => {
          state.page += 1;
          render();
        });
        render();
      })();
    </script>
    ${
      redoxTrendPlot.hasData
        ? `<script src="https://cdn.plot.ly/plotly-2.35.2.min.js"></script>
    <script>
      const reportRedoxStatusNode = document.getElementById("report-redox-status");
      if (!window.Plotly) {
        reportRedoxStatusNode.textContent = "Plot rendering failed because Plotly CDN was not reachable.";
      } else {
        const redoxTrendData = ${redoxTrendPlotDataJson};
        const redoxTrendLayout = ${redoxTrendPlotLayoutJson};
        const redoxTrendConfig = ${redoxTrendPlotConfigJson};
        window.Plotly.newPlot("report-redox-trend-plot", redoxTrendData, redoxTrendLayout, redoxTrendConfig).catch((error) => {
          reportRedoxStatusNode.textContent = "Plot rendering failed: " + (error?.message || String(error));
        });
      }
    </script>`
        : ""
    }
  </body>
</html>`;

    downloadText(html, buildExportFileName("color_report", "html"), "text/html");
  };

  const handleExportReportSpreadsheet = async () => {
    if (!reportBasis.length) {
      window.alert("No Color measurements are available for export.");
      return;
    }
    await ensureDetails(reportBasis.map((row) => row.id));

    const workbook = XLSX.utils.book_new();
    const measurementSheetRows = [
      [
        "Measured At",
        "Measurement Label",
        "Method",
        "Color",
        "Brightness (%)",
        "Thickness Mean (mm)",
        "x",
        "y",
        "L*",
        "a*",
        "b*",
        "Dominant / Complementary Wavelength (nm)",
        "Wavelength Mode",
        "Fe2 / Fe Total (%)",
        "Source CSV Path"
      ],
      ...reportBasis.map((row) => [
        formatAppDateTime(row.measured_at),
        row.measurement_label || "",
        row.method_name || "",
        row.glass_color || "",
        row.brightness_percent ?? "",
        row.thickness_mean_mm ?? "",
        row.x_chromaticity ?? "",
        row.y_chromaticity ?? "",
        row.l_star ?? "",
        row.a_star ?? "",
        row.b_star ?? "",
        row.dominant_wavelength_nm ?? "",
        row.wavelength_mode || "",
        row.fe2_fe_total_percent ?? "",
        row.source_csv_path || ""
      ])
    ];
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(measurementSheetRows), "Measurements");

    const curveRows = [
      ["Measurement", "Curve Kind", "Wavelength (nm)", "Value"],
      ...reportDetailBasis.flatMap((detail) =>
        Object.entries(detail.curves).flatMap(([kind, rows]) =>
          (rows || []).map((row) => [
            formatReferenceLabel(measurementMap.get(detail.id) || detail),
            kind,
            row.wavelength_nm,
            row.value
          ])
        )
      )
    ];
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(curveRows), "Curves");

    const workbookData = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    downloadBlob(
      new Blob([workbookData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      }),
      buildExportFileName("color_report", "xlsx")
    );
  };

  const handleExportReportCsv = async () => {
    if (!reportBasis.length) {
      window.alert("No Color measurements are available for export.");
      return;
    }
    const rows = [
      [
        "measured_at",
        "measurement_label",
        "method_name",
        "glass_color",
        "brightness_percent",
        "x_chromaticity",
        "y_chromaticity",
        "l_star",
        "a_star",
        "b_star",
        "dominant_wavelength_nm",
        "wavelength_mode",
        "thickness_mean_mm",
        "fe2_fe_total_percent",
        "source_csv_path"
      ],
      ...reportBasis.map((row) => [
        formatAppDateTime(row.measured_at),
        row.measurement_label || "",
        row.method_name || "",
        row.glass_color || "",
        row.brightness_percent != null ? String(row.brightness_percent) : "",
        row.x_chromaticity != null ? String(row.x_chromaticity) : "",
        row.y_chromaticity != null ? String(row.y_chromaticity) : "",
        row.l_star != null ? String(row.l_star) : "",
        row.a_star != null ? String(row.a_star) : "",
        row.b_star != null ? String(row.b_star) : "",
        row.dominant_wavelength_nm != null ? String(row.dominant_wavelength_nm) : "",
        row.wavelength_mode || "",
        row.thickness_mean_mm != null ? String(row.thickness_mean_mm) : "",
        row.fe2_fe_total_percent != null ? String(row.fe2_fe_total_percent) : "",
        row.source_csv_path || ""
      ])
    ]
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    downloadText(rows, buildExportFileName("color_report", "csv"), "text/csv");
  };

  const archiveEmptyMessage = loadError
    ? loadError
    : loading
      ? "Loading Color measurements..."
      : "No Color measurements found.";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="card" style={{ padding: 18 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 18,
            alignItems: "flex-start",
            flexWrap: "wrap"
          }}
        >
          <div style={{ maxWidth: 760 }}>
            <h2 className="section-title" style={{ fontSize: 24 }}>
              Color
            </h2>
            <p style={{ margin: "8px 0 0", color: "var(--text-subtle)" }}>
              Curve-first color measurement workspace for sync, comparison, trend review, reporting,
              and reference-data administration. Spectra and chromaticity charts render from
              database curve points only.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(["archive", "trend", "reports", "admin"] as PageTab[]).map((entry) => (
              <button
                key={entry}
                type="button"
                className="pill"
                aria-selected={tab === entry}
                onClick={() => setTab(entry)}
                style={{ cursor: "pointer" }}
              >
                {entry === "archive"
                  ? "Archive"
                  : entry === "trend"
                    ? "Trend"
                    : entry === "reports"
                      ? "Reports"
                      : "Admin"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {needsLogin && (
        <div className="card" style={{ borderColor: "var(--status-warning)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap"
            }}
          >
            <div>
              <div className="label">NAS Status</div>
              <div style={{ color: "var(--text-primary)" }}>
                {nasError || "NAS is not mounted. Archive data stays available from PostgREST, but Sync NAS requires a mount."}
              </div>
            </div>
            <button className="button secondary" onClick={() => setShowNasLogin(true)}>
              Mount NAS
            </button>
          </div>
        </div>
      )}

      {tab === "archive" && (
        <>
          <div
            className="card"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap"
            }}
          >
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span className="pill">Records: {filtered.length}</span>
              <span className="pill">Selected: {selectedIds.size}</span>
              <span className="pill">Curve: {COLOR_CURVE_LABELS[curveKind]}</span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button className="button" onClick={handleAnalyzeSelection} disabled={!selectedIds.size}>
                Analyze / Compare
              </button>
              <button className="button secondary" onClick={resetView}>
                Reset View
              </button>
              <button className="button secondary" onClick={() => setSyncOpen(true)}>
                Sync NAS
              </button>
            </div>
          </div>

          <ColorFilterPanel
            filters={effectiveDraftFilters}
            onChange={(nextFilters) => setDraftFilters(sanitizeColorFilters(nextFilters))}
            onApply={() => setFilters(sanitizeColorFilters(draftFilters))}
            onClear={() => {
              setDraftFilters(defaultColorFilters);
              setFilters(defaultColorFilters);
            }}
            methods={methodOptions}
            observers={observerRows}
            illuminants={illuminantRows}
            glassProperties={glassPropertyRows}
            glassColorOptions={glassColorOptions}
            trialOptions={trialOptions}
          />

          <ColorTable
            data={paginated}
            filteredIds={filtered.map((row) => row.id)}
            filteredTotal={filtered.length}
            selectedIds={selectedIds}
            onToggle={handleToggleSelection}
            onToggleFilteredSelection={(checked) =>
              handleToggleFilteredSelection(filtered.map((row) => row.id), checked)
            }
            onClearSelection={() => setSelectedIds(new Set())}
            page={page}
            pageSize={PAGE_SIZE}
            sort={sort}
            onSortChange={setSort}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onPageChange={setPage}
            onOpenDetail={openDetail}
            emptyMessage={archiveEmptyMessage}
          />

          <ColorCompareWorkspace
            selectedRows={selectedMeasurements}
            selectedDetails={selectedDetails}
            curveKind={curveKind}
            onCurveKindChange={setCurveKind}
            onLoadSelection={() => ensureDetails(selectedMeasurements.map((row) => row.id))}
            detailCache={detailCache}
          />
        </>
      )}

      {tab === "trend" && (
        <ColorTrendPanel
          rows={selectedMeasurements.length ? selectedMeasurements : filtered}
          selectedCount={selectedMeasurements.length}
        />
      )}

      {tab === "reports" && (
        <ColorReportPanel
          basisRows={reportBasis}
          hydratedRows={reportDetailBasis}
          onHydrate={() => ensureDetails(reportBasis.map((row) => row.id))}
          curveKind={curveKind}
          onCurveKindChange={setCurveKind}
          onExportHtml={handleExportReportHtml}
          onExportCsv={handleExportReportCsv}
          onExportXlsx={handleExportReportSpreadsheet}
        />
      )}

      {tab === "admin" && (
      <ColorAdminPanel
          observers={observerRows}
          illuminants={illuminantRows}
          transmittanceModes={transmittanceModeRows}
          reflectionModels={reflectionModelRows}
          glassProperties={glassPropertyRows}
          trialDurations={trialDurationRows}
          colorTrialMappings={colorTrialMappings}
          methods={methods}
          onObserversChanged={async (fn) => {
            await fn();
            clearCache();
            await loadAllData();
          }}
          onIlluminantsChanged={async (fn) => {
            await fn();
            clearCache();
            await loadAllData();
          }}
          onTransmittanceModesChanged={async (fn) => {
            await fn();
            clearCache();
            await loadAllData();
          }}
          onReflectionModelsChanged={async (fn) => {
            await fn();
            clearCache();
            await loadAllData();
          }}
          onMethodsChanged={async (fn) => {
            await fn();
            clearCache();
            await loadAllData();
          }}
          onColorTrialMappingsChanged={async (fn) => {
            await fn();
            clearCache();
            await loadAllData();
          }}
        />
      )}

      <ColorDetailModal
        open={!!detailId}
        measurement={detailMeasurement}
        detail={detailRecord}
        loading={detailLoading}
        onClose={() => setDetailId(null)}
      />

      <SyncModal
        open={syncOpen}
        running={syncRunning}
        error={syncError}
        logs={syncLogs}
        progress={syncProgress}
        dryRun={syncDryRun}
        clearExisting={syncClearExisting}
        methodId={syncMethodId}
        methods={methodOptions}
        folderFilter={syncFolderFilter}
        paused={syncPaused}
        stopRequested={syncStopRequested}
        exportingMissingRows={syncMissingRowsExporting}
        onClose={() => setSyncOpen(false)}
        onMethodChange={setSyncMethodId}
        onDryRunChange={setSyncDryRun}
        onClearExistingChange={setSyncClearExisting}
        onFolderFilterChange={setSyncFolderFilter}
        onStart={handleStartSync}
        onExportMissingRows={handleExportMissingThicknessRows}
        onPauseToggle={() => {
          const next = !syncPaused;
          syncControlRef.current.pause = next;
          setSyncPaused(next);
        }}
        onStop={() => {
          syncControlRef.current.stop = true;
          setSyncStopRequested(true);
        }}
        mountPoint={mountPoint ? joinNasPath(mountPoint, NAS_COLOR_INPUT_DIR) : null}
      />

      <NasLoginModal
        open={showNasLogin}
        credentials={nasCreds}
        onChange={setNasCreds}
        status={nasStatus}
        error={nasError}
        onSubmit={() => {
          setConfig(nasCreds);
          triggerMount(nasCreds)
            .then(() => setShowNasLogin(false))
            .catch(() => {
              /* hook surfaces mount errors */
            });
        }}
      />
    </div>
  );
}

function ColorFilterPanel({
  filters,
  onChange,
  onApply,
  onClear,
  methods,
  observers,
  illuminants,
  glassProperties,
  glassColorOptions,
  trialOptions
}: {
  filters: ColorFilters;
  onChange: (filters: ColorFilters) => void;
  onApply: () => void;
  onClear: () => void;
  methods: ColorMeasurementMethod[];
  observers: ColorObserver[];
  illuminants: ColorIlluminant[];
  glassProperties: ColorGlassProperty[];
  glassColorOptions: string[];
  trialOptions: Record<string, string[]>;
}) {
  const handleArrayToggle = (
    key: "methodIds" | "observerIds" | "illuminantIds" | "glassPropertyIds" | "glassColors",
    value: string
  ) => {
    const current = filters[key];
    const next = current.includes(value) ? current.filter((row) => row !== value) : [...current, value];
    onChange({
      ...filters,
      [key]: next
    });
  };

  const handleTrialToggle = (trialType: TrialType, name: string) => {
    const current = filters.trials[trialType] || [];
    const next = current.includes(name) ? current.filter((row) => row !== name) : [...current, name];
    onChange({
      ...filters,
      trials: {
        ...filters.trials,
        [trialType]: next
      }
    });
  };

  return (
    <div className="card">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "flex-end"
        }}
      >
        <div style={{ minWidth: 220 }}>
          <div className="label">Date</div>
          <input
            className="input"
            placeholder="YYYY-MM-DD or YYYY-MM-DD HH:MM:SS"
            value={filters.date || ""}
            onChange={(event) => onChange({ ...filters, date: event.target.value || null })}
            onBlur={(event) =>
              onChange({
                ...filters,
                date: normalizeDateFilterInput(event.target.value) || null
              })
            }
          />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="button" onClick={onApply}>
            Apply
          </button>
          <button className="button secondary" onClick={onClear}>
            Clear
          </button>
        </div>
      </div>

      <div className="divider" />

      <div className="grid two">
        <FilterChipGroup
          label="Methods"
          items={methods.map((row) => ({
            key: row.id,
            label: row.name,
            checked: filters.methodIds.includes(row.id),
            onToggle: () => handleArrayToggle("methodIds", row.id)
          }))}
        />
        <FilterChipGroup
          label="Observers"
          items={observers.map((row) => ({
            key: row.id,
            label: row.code,
            checked: filters.observerIds.includes(row.id),
            onToggle: () => handleArrayToggle("observerIds", row.id)
          }))}
        />
        <FilterChipGroup
          label="Illuminants"
          items={illuminants.map((row) => ({
            key: row.id,
            label: row.code,
            checked: filters.illuminantIds.includes(row.id),
            onToggle: () => handleArrayToggle("illuminantIds", row.id)
          }))}
        />
        <FilterChipGroup
          label="Optical Properties"
          items={glassProperties.map((row) => ({
            key: row.id,
            label: `${row.code} | ${row.name}`,
            checked: filters.glassPropertyIds.includes(row.id),
            onToggle: () => handleArrayToggle("glassPropertyIds", row.id)
          }))}
        />
        <FilterChipGroup
          label="Color"
          items={glassColorOptions.map((value) => ({
            key: value,
            label: value,
            checked: filters.glassColors.includes(value),
            onToggle: () => handleArrayToggle("glassColors", value)
          }))}
        />
        {Object.keys(trialOptions)
          .filter((trialType) => !COLOR_TRIAL_FILTERS_TO_HIDE.has(trialType) && (trialOptions[trialType] || []).length)
          .map((trialType) => (
          <FilterChipGroup
            key={trialType}
            label={`${trialType} Trials`}
            items={(trialOptions[trialType] || []).map((name) => ({
              key: name,
              label: name,
              checked: filters.trials[trialType]?.includes(name),
              onToggle: () => handleTrialToggle(trialType, name)
            }))}
          />
        ))}
      </div>
    </div>
  );
}

function FilterChipGroup({
  label,
  items
}: {
  label: string;
  items: Array<{
    key: string;
    label: string;
    checked: boolean | undefined;
    onToggle: () => void;
  }>;
}) {
  return (
    <div className="card" style={{ background: "var(--surface-elevated)", overflow: "hidden" }}>
      <div className="label">{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 132, overflowY: "auto" }}>
        {items.length ? (
          items.map((item) => (
            <label key={item.key} className="pill">
              <input type="checkbox" checked={!!item.checked} onChange={item.onToggle} />
              {item.label}
            </label>
          ))
        ) : (
          <span style={{ color: "var(--text-faint)" }}>No entries</span>
        )}
      </div>
    </div>
  );
}

function ColorTable({
  data,
  filteredIds,
  filteredTotal,
  selectedIds,
  onToggle,
  onToggleFilteredSelection,
  onClearSelection,
  page,
  pageSize,
  sort,
  onSortChange,
  searchTerm,
  onSearchChange,
  onPageChange,
  onOpenDetail,
  emptyMessage
}: {
  data: ColorMeasurement[];
  filteredIds: string[];
  filteredTotal: number;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onToggleFilteredSelection: (checked: boolean) => void;
  onClearSelection: () => void;
  page: number;
  pageSize: number;
  sort: ColorSortState;
  onSortChange: (state: ColorSortState) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onOpenDetail: (id: string) => void;
  emptyMessage: string;
}) {
  const totalPages = Math.max(1, Math.ceil(filteredTotal / pageSize));
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const selectedFilteredCount = filteredIds.reduce(
    (count, id) => (selectedIds.has(id) ? count + 1 : count),
    0
  );
  const allFilteredSelected = filteredIds.length > 0 && selectedFilteredCount === filteredIds.length;
  const partiallyFilteredSelected = selectedFilteredCount > 0 && !allFilteredSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = partiallyFilteredSelected;
    }
  }, [partiallyFilteredSelected]);

  const toggleSort = (field: ColorSortField) => {
    const nextDirection =
      sort.field === field && sort.direction === "asc" ? "desc" : "asc";
    onSortChange({ field, direction: nextDirection });
  };

  return (
    <div className="card">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 10,
          alignItems: "center",
          flexWrap: "wrap"
        }}
      >
        <div style={{ minWidth: 280 }}>
          <div className="label">Search</div>
          <input
            className="input"
            placeholder="Measurement label, method, source path, or date"
            value={searchTerm}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="button secondary" onClick={onClearSelection} disabled={!selectedIds.size}>
            Clear Selection
          </button>
          <span className="pill">Rows: {filteredTotal}</span>
          <span className="pill">Selected: {selectedIds.size}</span>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="table">
          <thead>
            <tr>
              <th>
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allFilteredSelected}
                  disabled={!filteredIds.length}
                  aria-label="Select all filtered rows across pages"
                  onChange={(event) => onToggleFilteredSelection(event.target.checked)}
                />
              </th>
              <SortableHeader label="Measured" field="measured_at" sort={sort} onToggle={toggleSort} />
              <SortableHeader label="Measurement" field="measurement" sort={sort} onToggle={toggleSort} />
              <SortableHeader label="Method" field="method" sort={sort} onToggle={toggleSort} />
              <SortableHeader label="Color" field="glass_color" sort={sort} onToggle={toggleSort} />
              <SortableHeader label="Thickness" field="thickness_mean_mm" sort={sort} onToggle={toggleSort} />
              <SortableHeader label="Brightness" field="brightness_percent" sort={sort} onToggle={toggleSort} />
              <SortableHeader label="Fe2/FeTotal" field="fe2_fe_total_percent" sort={sort} onToggle={toggleSort} />
              <SortableHeader label="Fe2/Fe3" field="fe2_fe3_ratio" sort={sort} onToggle={toggleSort} />
              <SortableHeader label="Wavelength" field="dominant_wavelength_nm" sort={sort} onToggle={toggleSort} />
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {data.length ? (
              data.map((row) => {
                const selected = selectedIds.has(row.id);
                return (
                  <tr
                    key={row.id}
                    style={{
                      background: selected ? "rgba(204, 102, 51, 0.08)" : "transparent"
                    }}
                  >
                    <td>
                      <input type="checkbox" checked={selected} onChange={() => onToggle(row.id)} />
                    </td>
                    <td>{formatAppDateTime(row.measured_at)}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{formatReferenceLabel(row)}</div>
                      <div style={{ color: "var(--text-faint)", fontSize: 12 }}>{row.source_csv_path || "—"}</div>
                    </td>
                    <td>{row.method_name || "—"}</td>
                    <td>{row.glass_color || "—"}</td>
                    <td>{formatMetric(row.thickness_mean_mm, 3)}</td>
                    <td>{formatPercent(row.brightness_percent, 2)}</td>
                    <td>{formatPercent(row.fe2_fe_total_percent, 2)}</td>
                    <td>{formatMetric(row.fe2_fe3_ratio, 3)}</td>
                    <td>{formatWavelength(row.dominant_wavelength_nm, row.wavelength_mode)}</td>
                    <td>
                      <button className="button secondary" onClick={() => onOpenDetail(row.id)}>
                        Detail
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={11} style={{ textAlign: "center", color: "var(--text-muted)" }}>
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
        <button
          className="button secondary"
          disabled={page === 0}
          onClick={() => onPageChange(Math.max(0, page - 1))}
        >
          Prev
        </button>
        <span className="pill">
          Page {page + 1} / {totalPages}
        </span>
        <button
          className="button secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function SortableHeader({
  label,
  field,
  sort,
  onToggle
}: {
  label: string;
  field: ColorSortField;
  sort: ColorSortState;
  onToggle: (field: ColorSortField) => void;
}) {
  const active = sort.field === field;
  return (
    <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => onToggle(field)}>
      {label} {active ? (sort.direction === "asc" ? "▲" : "▼") : ""}
    </th>
  );
}

function ColorCompareWorkspace({
  selectedRows,
  selectedDetails,
  curveKind,
  onCurveKindChange,
  onLoadSelection,
  detailCache
}: {
  selectedRows: ColorMeasurement[];
  selectedDetails: ColorMeasurementDetail[];
  curveKind: ColorCurveKind;
  onCurveKindChange: (kind: ColorCurveKind) => void;
  onLoadSelection: () => void;
  detailCache: Record<string, ColorMeasurementDetail>;
}) {
  const hydratedCount = selectedDetails.length;
  const hasChromaticity = selectedRows.some(
    (row) => Number.isFinite(row.x_chromaticity) && Number.isFinite(row.y_chromaticity)
  );
  const hasLab = selectedRows.some(
    (row) => Number.isFinite(row.l_star) && Number.isFinite(row.a_star) && Number.isFinite(row.b_star)
  );
  const viewingConditions = summarizeViewingConditions(selectedRows);
  const compareTheme = getPlotTheme();
  const spectrumSeries = selectedRows
    .map((row, index) => {
      const detail = detailCache[row.id];
      const points = getCurvePoints(detail, curveKind);
      if (!points.length) return null;
      return {
        id: row.id,
        label: formatReferenceLabel(row),
        color: colorForIndex(index),
        points
      };
    })
    .filter(isPresent);

  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center"
        }}
      >
        <div>
          <h3 className="section-title">Compare Workspace</h3>
          <p style={{ margin: "4px 0 0", color: "var(--text-subtle)" }}>
            Overlay the selected measurements as spectra, chromaticity points, and CIELAB values.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select
            className="select"
            value={curveKind}
            onChange={(event) => onCurveKindChange(event.target.value as ColorCurveKind)}
            style={{ minWidth: 220 }}
          >
            {(Object.keys(COLOR_CURVE_LABELS) as ColorCurveKind[]).map((kind) => (
              <option key={kind} value={kind}>
                {COLOR_CURVE_LABELS[kind]}
              </option>
            ))}
          </select>
          <button className="button secondary" onClick={onLoadSelection} disabled={!selectedRows.length}>
            Load Curves
          </button>
        </div>
      </div>

      {!selectedRows.length ? (
        <div className="card" style={{ background: "var(--surface-elevated)" }}>
          Select measurements from the archive table to activate comparison.
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span className="pill">Selected: {selectedRows.length}</span>
            <span className="pill">Hydrated Curves: {hydratedCount}</span>
            <span className="pill">Mode: {COLOR_CURVE_LABELS[curveKind]}</span>
            <span className="pill">Conditions: {viewingConditions.label}</span>
          </div>

          {viewingConditions.warning ? (
            <div className="card" style={{ borderColor: "var(--status-warning)" }}>
              <span style={{ color: "var(--status-warning)" }}>{viewingConditions.warning}</span>
            </div>
          ) : null}

          <div className="card" style={buildProfessionalPlotCardStyle()}>
            <div className="label">Spectrum Overlay</div>
            <Plot
              data={spectrumSeries.map((series) => ({
                x: series.points.map((point) => point.wavelength_nm),
                y: series.points.map((point) => point.value),
                type: "scatter",
                mode: "lines",
                name: series.label,
                hovertemplate: `${series.label}<br>%{x:.1f} nm<br>%{y:.4f}<extra></extra>`,
                line: {
                  color: series.color,
                  width: 2.8
                }
              }))}
              layout={{ ...buildSpectrumLayout(COLOR_CURVE_LABELS[curveKind]), showlegend: false }}
              config={{ responsive: true, displaylogo: false }}
              style={{ width: "100%", height: 360 }}
            />
            {spectrumSeries.length ? (
              <div
                style={{
                  marginTop: 10,
                  maxHeight: "calc(4 * 20px + 3 * 8px + 16px)",
                  overflowY: "auto",
                  background: withAlpha(compareTheme.surface, 0.72, "rgba(23,33,52,0.72)"),
                  border: `1px solid ${withAlpha(compareTheme.surfaceBorder, 0.65, "rgba(48,72,106,0.65)")}`,
                  borderRadius: 8,
                  padding: "8px 10px"
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                    gridAutoRows: "20px",
                    gap: "8px 16px"
                  }}
                >
                  {spectrumSeries.map((series) => (
                    <div key={series.id} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      <span
                        style={{
                          width: 22,
                          height: 2,
                          background: series.color,
                          borderRadius: 999,
                          flexShrink: 0
                        }}
                      />
                      <span
                        title={series.label}
                        style={{
                          color: "var(--text-muted)",
                          fontSize: 12,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                      >
                        {series.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="grid two">
            <div className="card" style={buildProfessionalPlotCardStyle()}>
              <div className="label">Chromaticity Diagram ({viewingConditions.label})</div>
              {viewingConditions.mixed ? (
                <div style={{ color: "var(--text-muted)", padding: "24px 8px" }}>
                  Select rows with one observer and one illuminant to compare chromaticity.
                </div>
              ) : hasChromaticity ? (
                <Plot
                  data={buildChromaticityPlotData(selectedRows, null)}
                  layout={buildChromaticityLayout(selectedRows, null)}
                  config={{ responsive: true, displaylogo: false }}
                  style={{ width: "100%", height: 380 }}
                />
              ) : (
                <div style={{ color: "var(--text-muted)", padding: "24px 8px" }}>
                  No chromaticity coordinates are available for the selected measurements.
                </div>
              )}
            </div>

            <div className="card" style={buildProfessionalPlotCardStyle()}>
              <div className="label">L*a*b* Color Space ({viewingConditions.label})</div>
              {viewingConditions.mixed ? (
                <div style={{ color: "var(--text-muted)", padding: "24px 8px" }}>
                  Select rows with one observer and one illuminant to compare CIELAB positions.
                </div>
              ) : hasLab ? (
                <Plot
                  data={buildLabPlotData(selectedRows, null)}
                  layout={buildLabLayout(selectedRows, null)}
                  config={{ responsive: true, displaylogo: false }}
                  style={{ width: "100%", height: 380 }}
                />
              ) : (
                <div style={{ color: "var(--text-muted)", padding: "24px 8px" }}>
                  CIELAB values are not available for the selected measurements yet.
                </div>
              )}
            </div>
          </div>

        </>
      )}
    </div>
  );
}

function ColorTrendPanel({
  rows,
  selectedCount
}: {
  rows: ColorMeasurement[];
  selectedCount: number;
}) {
  const ABSORBANCE_OVERLAY_WAVELENGTH_NM = REDOX_TIME_OVERLAY_ABSORBANCE_WAVELENGTH_NM;
  const ABSORBANCE_OVERLAY_SCALE = REDOX_TIME_OVERLAY_ABSORBANCE_SCALE;
  const REDOX_SCATTER_LABEL = "Fe2 / FeTotal vs Fe2O3 input (XRF) + linear fit";
  type TrendPlotMode =
    | keyof ColorMeasurement
    | "fe2_fe_total_vs_input_fe2o3_time"
    | "fe2_fe_total_vs_input_fe2o3_scatter";

  const [metricKey, setMetricKey] = useState<TrendPlotMode>("brightness_percent");
  const trendTheme = getPlotTheme();
  const sortedRows = useMemo(() => [...rows].sort(compareByTimeAscending), [rows]);
  const [absorbancePerMmAt400ById, setAbsorbancePerMmAt400ById] = useState<Record<string, number | null>>({});
  const [absorbancePerMmAt400Loading, setAbsorbancePerMmAt400Loading] = useState(false);
  const isRedoxTimeOverlay = metricKey === "fe2_fe_total_vs_input_fe2o3_time";
  const isRedoxScatter = metricKey === "fe2_fe_total_vs_input_fe2o3_scatter";
  const absorbanceSourceIds = useMemo(
    () => (isRedoxTimeOverlay ? sortedRows.map((row) => row.id) : []),
    [isRedoxTimeOverlay, sortedRows]
  );
  const absorbanceMissingIds = useMemo(
    () => absorbanceSourceIds.filter((id) => !(id in absorbancePerMmAt400ById)),
    [absorbanceSourceIds, absorbancePerMmAt400ById]
  );

  useEffect(() => {
    if (!isRedoxTimeOverlay || !absorbanceMissingIds.length) return;
    let cancelled = false;
    setAbsorbancePerMmAt400Loading(true);
    fetchColorAbsorbancePerMmAtWavelength(absorbanceMissingIds, ABSORBANCE_OVERLAY_WAVELENGTH_NM)
      .then((loaded) => {
        if (cancelled) return;
        setAbsorbancePerMmAt400ById((prev) => ({
          ...prev,
          ...loaded
        }));
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (!cancelled) {
          setAbsorbancePerMmAt400Loading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isRedoxTimeOverlay, absorbanceMissingIds]);

  const metricOption =
    COLOR_TREND_METRICS.find((row) => row.key === metricKey) || null;
  const plotRows = metricOption
    ? sortedRows.filter((row) => toFiniteNumber(row[metricOption.key]) != null)
    : [];
  const fe2FeTotalRows = sortedRows.filter((row) => toFiniteNumber(row.fe2_fe_total_percent) != null);
  const xrfFe2O3Rows = sortedRows.filter((row) => toFiniteNumber(row.input_fe2o3_percent) != null);
  const fe2PercentRows = sortedRows.filter((row) => toFiniteNumber(row.fe2_percent) != null);
  const feTotalPercentRows = sortedRows.filter((row) => toFiniteNumber(row.fe_total_percent) != null);
  const absorbancePerMm400Rows = sortedRows
    .map((row) => ({
      row,
      value: (() => {
        const rawValue = toFiniteNumber(absorbancePerMmAt400ById[row.id]);
        return rawValue == null ? null : rawValue * ABSORBANCE_OVERLAY_SCALE;
      })()
    }))
    .filter((entry): entry is { row: ColorMeasurement; value: number } => entry.value != null);

  const values = plotRows
    .map((row) => (metricOption ? toFiniteNumber(row[metricOption.key]) : null))
    .filter((value): value is number => value != null);
  const min = values.length ? Math.min(...values) : null;
  const max = values.length ? Math.max(...values) : null;
  const fe2FeTotalValues = fe2FeTotalRows
    .map((row) => toFiniteNumber(row.fe2_fe_total_percent))
    .filter((value): value is number => value != null);
  const xrfFe2O3Values = xrfFe2O3Rows
    .map((row) => toFiniteNumber(row.input_fe2o3_percent))
    .filter((value): value is number => value != null);
  const fe2PercentValues = fe2PercentRows
    .map((row) => toFiniteNumber(row.fe2_percent))
    .filter((value): value is number => value != null);
  const feTotalPercentValues = feTotalPercentRows
    .map((row) => toFiniteNumber(row.fe_total_percent))
    .filter((value): value is number => value != null);
  const absorbancePerMm400Values = absorbancePerMm400Rows.map((entry) => entry.value);
  const fe2FeTotalMin = fe2FeTotalValues.length ? Math.min(...fe2FeTotalValues) : null;
  const fe2FeTotalMax = fe2FeTotalValues.length ? Math.max(...fe2FeTotalValues) : null;
  const xrfFe2O3Min = xrfFe2O3Values.length ? Math.min(...xrfFe2O3Values) : null;
  const xrfFe2O3Max = xrfFe2O3Values.length ? Math.max(...xrfFe2O3Values) : null;
  const fe2PercentMin = fe2PercentValues.length ? Math.min(...fe2PercentValues) : null;
  const fe2PercentMax = fe2PercentValues.length ? Math.max(...fe2PercentValues) : null;
  const feTotalPercentMin = feTotalPercentValues.length ? Math.min(...feTotalPercentValues) : null;
  const feTotalPercentMax = feTotalPercentValues.length ? Math.max(...feTotalPercentValues) : null;
  const absorbancePerMm400Min = absorbancePerMm400Values.length
    ? Math.min(...absorbancePerMm400Values)
    : null;
  const absorbancePerMm400Max = absorbancePerMm400Values.length
    ? Math.max(...absorbancePerMm400Values)
    : null;
  const hasRedoxXrfPlot =
    fe2FeTotalRows.length > 0 ||
    xrfFe2O3Rows.length > 0 ||
    fe2PercentRows.length > 0 ||
    feTotalPercentRows.length > 0 ||
    absorbancePerMm400Rows.length > 0;
  const redoxVsXrfRows = sortedRows.filter(
    (row) =>
      toFiniteNumber(row.input_fe2o3_percent) != null &&
      toFiniteNumber(row.fe2_fe_total_percent) != null
  );
  const redoxVsXrfPoints = redoxVsXrfRows.map((row) => ({
    x: toFiniteNumber(row.input_fe2o3_percent) as number,
    y: toFiniteNumber(row.fe2_fe_total_percent) as number,
    label: `${formatReferenceLabel(row)}<br>${formatAppDateTime(row.measured_at)}`,
    color: displayColorForMeasurement(row, opticalPropertyTrendColor(row.glass_property_code))
  }));
  const hasRedoxVsXrfScatter = redoxVsXrfPoints.length > 0;
  const redoxVsXrfRegression = (() => {
    if (redoxVsXrfPoints.length < 2) return null;
    const xs = redoxVsXrfPoints.map((point) => point.x);
    const ys = redoxVsXrfPoints.map((point) => point.y);
    const meanX = xs.reduce((sum, value) => sum + value, 0) / xs.length;
    const meanY = ys.reduce((sum, value) => sum + value, 0) / ys.length;

    let numerator = 0;
    let denominator = 0;
    xs.forEach((x, index) => {
      const dx = x - meanX;
      numerator += dx * (ys[index] - meanY);
      denominator += dx * dx;
    });
    if (Math.abs(denominator) < 1e-12) return null;

    const slope = numerator / denominator;
    const intercept = meanY - slope * meanX;
    let rss = 0;
    let tss = 0;
    ys.forEach((y, index) => {
      const fitted = slope * xs[index] + intercept;
      const residual = y - fitted;
      rss += residual * residual;
      const centered = y - meanY;
      tss += centered * centered;
    });
    const r2 = tss > 0 ? 1 - rss / tss : null;
    const xMin = Math.min(...xs);
    const xMax = Math.max(...xs);
    return { slope, intercept, r2, xMin, xMax };
  })();
  const redoxVsXrfFitEquation = redoxVsXrfRegression
    ? `y = ${formatMetric(redoxVsXrfRegression.slope, 4)}x ${
        redoxVsXrfRegression.intercept >= 0 ? "+" : "-"
      } ${formatMetric(Math.abs(redoxVsXrfRegression.intercept), 4)}`
    : "Fit unavailable";
  const redoxComparisonLayout = (() => {
    const base = buildTrendLayout("Fe2 / FeTotal (%)");
    return {
      ...base,
      yaxis: {
        ...base.yaxis,
        title: `Fe2 / FeTotal (%) and A${ABSORBANCE_OVERLAY_WAVELENGTH_NM} Abs/mm ×${ABSORBANCE_OVERLAY_SCALE}`
      },
      yaxis2: {
        title: "Fe2O3 input / Fe2 / Fe total (%)",
        overlaying: "y",
        side: "right",
        showgrid: false,
        showline: true,
        linecolor: withAlpha(trendTheme.textPrimary, 0.64, "rgba(236,241,255,0.64)"),
        ticks: "outside",
        tickcolor: withAlpha(trendTheme.textPrimary, 0.64, "rgba(236,241,255,0.64)")
      }
    };
  })();
  const redoxVsXrfLayout = (() => {
    const base = buildTrendLayout("Fe2 / FeTotal (%)");
    return {
      ...base,
      xaxis: {
        ...base.xaxis,
        title: "Fe2O3 Input (XRF) (%)"
      },
      yaxis: {
        ...base.yaxis,
        title: "Fe2 / FeTotal (%)"
      }
    };
  })();
  const activePlot = (() => {
    if (isRedoxTimeOverlay) {
      return {
        hasData: hasRedoxXrfPlot,
        emptyMessage:
          "No Fe2/FeTotal, Fe2O3 input, Fe2, Fe total, or A400 Abs/mm values are available for the current scope.",
        layout: redoxComparisonLayout,
        data: [
          {
            x: fe2FeTotalRows.map((row) => row.measured_at),
            y: fe2FeTotalRows.map((row) => toFiniteNumber(row.fe2_fe_total_percent) as number),
            text: fe2FeTotalRows.map((row) => formatReferenceLabel(row)),
            type: "scatter",
            mode: "lines+markers",
            name: "Fe2 / FeTotal",
            marker: {
              color: trendTheme.accentOne,
              size: 8,
              line: {
                color: withAlpha(trendTheme.textPrimary, 0.45, "rgba(236,241,255,0.45)"),
                width: 1.1
              }
            },
            line: {
              color: trendTheme.accentOne,
              width: 2.8,
              shape: "spline",
              smoothing: 0.45
            },
            hovertemplate: "%{text}<br>%{x}<br>Fe2/FeTotal: %{y:.4f}<extra></extra>",
            yaxis: "y"
          },
          {
            x: xrfFe2O3Rows.map((row) => row.measured_at),
            y: xrfFe2O3Rows.map((row) => toFiniteNumber(row.input_fe2o3_percent) as number),
            text: xrfFe2O3Rows.map((row) => formatReferenceLabel(row)),
            type: "scatter",
            mode: "lines+markers",
            name: "Fe2O3 input (XRF)",
            marker: {
              color: trendTheme.accentTwo,
              size: 8,
              symbol: "diamond",
              line: {
                color: withAlpha(trendTheme.textPrimary, 0.45, "rgba(236,241,255,0.45)"),
                width: 1.1
              }
            },
            line: {
              color: trendTheme.accentTwo,
              width: 2.6,
              shape: "spline",
              smoothing: 0.45,
              dash: "dot"
            },
            hovertemplate: "%{text}<br>%{x}<br>Fe2O3 input: %{y:.4f}<extra></extra>",
            yaxis: "y2"
          },
          {
            x: fe2PercentRows.map((row) => row.measured_at),
            y: fe2PercentRows.map((row) => toFiniteNumber(row.fe2_percent) as number),
            text: fe2PercentRows.map((row) => formatReferenceLabel(row)),
            type: "scatter",
            mode: "lines+markers",
            name: "Fe2 percent",
            marker: {
              color: "#ff8a00",
              size: 8,
              symbol: "square",
              line: {
                color: withAlpha(trendTheme.textPrimary, 0.45, "rgba(236,241,255,0.45)"),
                width: 1.1
              }
            },
            line: {
              color: "#ff8a00",
              width: 2.2,
              shape: "spline",
              smoothing: 0.45,
              dash: "dash"
            },
            hovertemplate: "%{text}<br>%{x}<br>Fe2 percent: %{y:.4f}<extra></extra>",
            yaxis: "y2"
          },
          {
            x: feTotalPercentRows.map((row) => row.measured_at),
            y: feTotalPercentRows.map((row) => toFiniteNumber(row.fe_total_percent) as number),
            text: feTotalPercentRows.map((row) => formatReferenceLabel(row)),
            type: "scatter",
            mode: "lines+markers",
            name: "Fe total percent",
            marker: {
              color: "#a855f7",
              size: 8,
              symbol: "circle-open",
              line: {
                color: withAlpha(trendTheme.textPrimary, 0.45, "rgba(236,241,255,0.45)"),
                width: 1.1
              }
            },
            line: {
              color: "#a855f7",
              width: 2.1,
              shape: "spline",
              smoothing: 0.45,
              dash: "dot"
            },
            hovertemplate: "%{text}<br>%{x}<br>Fe total percent: %{y:.4f}<extra></extra>",
            yaxis: "y2"
          },
          {
            x: absorbancePerMm400Rows.map((entry) => entry.row.measured_at),
            y: absorbancePerMm400Rows.map((entry) => entry.value),
            text: absorbancePerMm400Rows.map((entry) => formatReferenceLabel(entry.row)),
            type: "scatter",
            mode: "lines+markers",
            name: `Absorbance/mm @ ${ABSORBANCE_OVERLAY_WAVELENGTH_NM} nm ×${ABSORBANCE_OVERLAY_SCALE}`,
            marker: {
              color: "#f43f5e",
              size: 8,
              symbol: "triangle-up",
              line: {
                color: withAlpha(trendTheme.textPrimary, 0.45, "rgba(236,241,255,0.45)"),
                width: 1.1
              }
            },
            line: {
              color: "#f43f5e",
              width: 2.1,
              shape: "spline",
              smoothing: 0.45,
              dash: "longdash"
            },
            hovertemplate: `%{text}<br>%{x}<br>Abs/mm @ ${ABSORBANCE_OVERLAY_WAVELENGTH_NM} nm ×${ABSORBANCE_OVERLAY_SCALE}: %{y:.4f}<extra></extra>`,
            yaxis: "y"
          }
        ]
      };
    }
    if (isRedoxScatter) {
      return {
        hasData: hasRedoxVsXrfScatter,
        emptyMessage: "No paired Fe2O3 input and Fe2/FeTotal values are available for the current scope.",
        layout: redoxVsXrfLayout,
        data: [
          {
            x: redoxVsXrfPoints.map((point) => point.x),
            y: redoxVsXrfPoints.map((point) => point.y),
            text: redoxVsXrfPoints.map((point) => point.label),
            type: "scatter",
            mode: "markers",
            name: "Measured points",
            marker: {
              color: redoxVsXrfPoints.map((point) => point.color),
              size: 10,
              line: {
                color: withAlpha(trendTheme.textPrimary, 0.45, "rgba(236,241,255,0.45)"),
                width: 1.2
              }
            },
            hovertemplate:
              "%{text}<br>Fe2O3 input: %{x:.4f}<br>Fe2/FeTotal: %{y:.4f}<extra></extra>"
          },
          ...(redoxVsXrfRegression
            ? [
                {
                  x: [redoxVsXrfRegression.xMin, redoxVsXrfRegression.xMax],
                  y: [
                    redoxVsXrfRegression.slope * redoxVsXrfRegression.xMin +
                      redoxVsXrfRegression.intercept,
                    redoxVsXrfRegression.slope * redoxVsXrfRegression.xMax +
                      redoxVsXrfRegression.intercept
                  ],
                  type: "scatter",
                  mode: "lines",
                  name: `Linear fit (R²=${formatMetric(redoxVsXrfRegression.r2, 4)})`,
                  line: {
                    color: trendTheme.accentThree,
                    width: 2.2,
                    dash: "dash"
                  },
                  hovertemplate:
                    "Linear fit<br>Fe2O3 input: %{x:.4f}<br>Fe2/FeTotal (fit): %{y:.4f}<extra></extra>"
                }
              ]
            : [])
        ]
      };
    }
    return {
      hasData: plotRows.length > 0,
      emptyMessage: "No values are available for the selected metric in the current scope.",
      layout: buildTrendLayout(metricOption?.label || "Trend Metric"),
      data: [
        {
          x: plotRows.map((row) => row.measured_at),
          y: plotRows.map((row) => (metricOption ? toFiniteNumber(row[metricOption.key]) : null) as number),
          text: plotRows.map((row) => formatReferenceLabel(row)),
          type: "scatter",
          mode: "lines+markers",
          marker: {
            color: plotRows.map((row) =>
              displayColorForMeasurement(row, opticalPropertyTrendColor(row.glass_property_code))
            ),
            size: 10,
            line: {
              color: withAlpha(trendTheme.textPrimary, 0.45, "rgba(236,241,255,0.45)"),
              width: 1.2
            }
          },
          line: {
            color: trendTheme.accentOne,
            width: 2.8,
            shape: "spline",
            smoothing: 0.55
          },
          hovertemplate: "%{text}<br>%{x}<br>%{y:.4f}<extra></extra>",
          name: metricOption?.label || "Metric"
        }
      ]
    };
  })();
  const trendSelectionLabel = isRedoxTimeOverlay
    ? REDOX_TIME_OVERLAY_LABEL
    : isRedoxScatter
      ? REDOX_SCATTER_LABEL
      : metricOption?.label || "Trend Metric";
  const handleExportTrendHtml = () => {
    if (!activePlot.hasData) {
      window.alert("No trend points are available for export.");
      return;
    }

    const trendBasis = selectedCount ? "Selected rows" : "Filtered rows";
    const exportTimestamp = formatCurrentDateTime();
    const traces = (activePlot.data as Array<{ name?: unknown; x?: unknown[]; y?: unknown[] }>)
      .map((trace, index) => {
        const count = Array.isArray(trace.x)
          ? trace.x.length
          : Array.isArray(trace.y)
            ? trace.y.length
            : 0;
        return {
          name:
            typeof trace.name === "string" && trace.name.trim()
              ? trace.name
              : `Series ${index + 1}`,
          count
        };
      })
      .filter((trace) => trace.count > 0);

    const summaryRows = [
      ["Trend", trendSelectionLabel],
      ["Basis", trendBasis],
      ["Rows in scope", String(sortedRows.length)],
      ["Exported at", exportTimestamp]
    ];
    const seriesRows = traces.length
      ? traces.map((trace) => [trace.name, String(trace.count)])
      : [["No series", "0"]];
    const exportLayout = {
      ...activePlot.layout,
      title: trendSelectionLabel,
      margin: {
        l: 64,
        r: 58,
        t: 64,
        b: 72
      }
    };
    const plotDataJson = serializeForInlineScript(activePlot.data);
    const plotLayoutJson = serializeForInlineScript(exportLayout);
    const plotConfigJson = serializeForInlineScript({
      responsive: true,
      displaylogo: false
    });
    const exportSlug = String(metricKey).toLowerCase().replace(/[^a-z0-9]+/g, "_");

    const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Color Trend Export</title>
    <style>
      :root {
        --bg: #f1f5fb;
        --surface: #ffffff;
        --ink: #122036;
        --muted: #5d6c84;
        --line: #d7e0ee;
        --accent: #0f4d8a;
        --panel: #0f1e35;
      }
      body {
        margin: 0;
        font-family: ${PLOT_FONT_FAMILY};
        color: var(--ink);
        background: radial-gradient(circle at 100% 0, #dbe8ff 0%, #edf3fb 56%, #e8eef7 100%);
      }
      .shell {
        max-width: 1280px;
        margin: 0 auto;
        padding: 24px;
      }
      .hero {
        background: linear-gradient(135deg, #0c2a52 0%, #0f4d8a 54%, #0e7f76 100%);
        color: #ffffff;
        border-radius: 18px;
        padding: 20px 22px;
        box-shadow: 0 20px 44px rgba(12, 42, 82, 0.2);
      }
      .hero h1 {
        margin: 0;
        font-size: 25px;
      }
      .hero-meta {
        margin-top: 6px;
        color: rgba(255, 255, 255, 0.86);
        font-size: 13px;
      }
      .section {
        margin-top: 16px;
        background: var(--surface);
        border: 1px solid var(--line);
        border-radius: 14px;
        padding: 14px;
        box-shadow: 0 14px 28px rgba(15, 23, 42, 0.07);
      }
      .section h2 {
        margin: 0 0 10px;
        font-size: 17px;
      }
      table {
        width: 100%;
        border-collapse: collapse;
      }
      th, td {
        border: 1px solid var(--line);
        text-align: left;
        vertical-align: top;
        padding: 8px 10px;
        font-size: 12px;
      }
      th {
        background: #edf4ff;
        color: #174074;
      }
      #plot-wrap {
        border-radius: 12px;
        overflow: hidden;
        border: 1px solid rgba(79, 181, 255, 0.28);
        background: var(--panel);
      }
      #status {
        margin-top: 10px;
        font-size: 12px;
        color: #9f1239;
      }
      .grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 14px;
      }
      @media (max-width: 980px) {
        .grid {
          grid-template-columns: 1fr;
        }
      }
    </style>
  </head>
  <body>
    <div class="shell">
      <div class="hero">
        <h1>Color Trend Export</h1>
        <p class="hero-meta">${escapeHtml(trendSelectionLabel)}</p>
      </div>
      <div class="grid">
        <div class="section">
          <h2>Export Summary</h2>
          ${renderHtmlTable(["Field", "Value"], summaryRows)}
        </div>
        <div class="section">
          <h2>Series Coverage</h2>
          ${renderHtmlTable(["Series", "Points"], seriesRows)}
        </div>
      </div>
      <div class="section">
        <h2>Trend Plot</h2>
        <div id="plot-wrap">
          <div id="trend-plot" style="width:100%;height:680px;"></div>
        </div>
        <div id="status"></div>
      </div>
    </div>

    <script src="https://cdn.plot.ly/plotly-2.35.2.min.js"></script>
    <script>
      const statusNode = document.getElementById("status");
      if (!window.Plotly) {
        statusNode.textContent = "Plot rendering failed because Plotly CDN was not reachable.";
      } else {
        const data = ${plotDataJson};
        const layout = ${plotLayoutJson};
        const config = ${plotConfigJson};
        window.Plotly.newPlot("trend-plot", data, layout, config).catch((error) => {
          statusNode.textContent = "Plot rendering failed: " + (error?.message || String(error));
        });
      }
    </script>
  </body>
</html>`;

    downloadText(html, buildExportFileName(`color_trend_${exportSlug}`, "html"), "text/html");
  };

  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center"
        }}
      >
        <div>
          <h3 className="section-title">Trend</h3>
          <p style={{ margin: "4px 0 0", color: "var(--text-subtle)" }}>
            Charts persisted scalar metrics over time for the current filtered dataset, or the
            selected subset when rows are selected in the archive.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select
            className="select"
            value={String(metricKey)}
            onChange={(event) => setMetricKey(event.target.value as TrendPlotMode)}
            style={{ minWidth: 260 }}
          >
            {COLOR_TREND_METRICS.map((metric) => (
              <option key={String(metric.key)} value={String(metric.key)}>
                {metric.label}
              </option>
            ))}
            <option value="fe2_fe_total_vs_input_fe2o3_time">
              {REDOX_TIME_OVERLAY_LABEL}
            </option>
            <option value="fe2_fe_total_vs_input_fe2o3_scatter">
              {REDOX_SCATTER_LABEL}
            </option>
          </select>
          <button
            className="button secondary export-action"
            onClick={handleExportTrendHtml}
            disabled={!activePlot.hasData}
          >
            Export Trend HTML
          </button>
          <span className="pill">Basis: {selectedCount ? "Selected rows" : "Filtered rows"}</span>
          {!isRedoxTimeOverlay && !isRedoxScatter && (
            <>
              <span className="pill">Points: {plotRows.length}</span>
              <span className="pill">
                Range: {min == null ? "—" : formatMetric(min, 4)} to {max == null ? "—" : formatMetric(max, 4)}
              </span>
            </>
          )}
          {isRedoxTimeOverlay && (
            <>
              <span className="pill">Fe2/FeTotal points: {fe2FeTotalRows.length}</span>
              <span className="pill">Fe2O3 input points: {xrfFe2O3Rows.length}</span>
              <span className="pill">Fe2 points: {fe2PercentRows.length}</span>
              <span className="pill">Fe total points: {feTotalPercentRows.length}</span>
              <span className="pill">
                Abs/mm@{ABSORBANCE_OVERLAY_WAVELENGTH_NM}×{ABSORBANCE_OVERLAY_SCALE} points:{" "}
                {absorbancePerMm400Rows.length}
                {absorbancePerMmAt400Loading ? " (loading)" : ""}
              </span>
              <span className="pill">
                Fe2/FeTotal range: {fe2FeTotalMin == null ? "—" : formatMetric(fe2FeTotalMin, 4)} to{" "}
                {fe2FeTotalMax == null ? "—" : formatMetric(fe2FeTotalMax, 4)}
              </span>
              <span className="pill">
                Fe2O3 input range: {xrfFe2O3Min == null ? "—" : formatMetric(xrfFe2O3Min, 4)} to{" "}
                {xrfFe2O3Max == null ? "—" : formatMetric(xrfFe2O3Max, 4)}
              </span>
              <span className="pill">
                Fe2 range: {fe2PercentMin == null ? "—" : formatMetric(fe2PercentMin, 4)} to{" "}
                {fe2PercentMax == null ? "—" : formatMetric(fe2PercentMax, 4)}
              </span>
              <span className="pill">
                Fe total range: {feTotalPercentMin == null ? "—" : formatMetric(feTotalPercentMin, 4)} to{" "}
                {feTotalPercentMax == null ? "—" : formatMetric(feTotalPercentMax, 4)}
              </span>
              <span className="pill">
                Abs/mm@{ABSORBANCE_OVERLAY_WAVELENGTH_NM}×{ABSORBANCE_OVERLAY_SCALE} range:{" "}
                {absorbancePerMm400Min == null ? "—" : formatMetric(absorbancePerMm400Min, 4)} to{" "}
                {absorbancePerMm400Max == null ? "—" : formatMetric(absorbancePerMm400Max, 4)}
              </span>
            </>
          )}
          {isRedoxScatter && (
            <>
              <span className="pill">Paired points: {redoxVsXrfPoints.length}</span>
              <span className="pill">
                X range: {xrfFe2O3Min == null ? "—" : formatMetric(xrfFe2O3Min, 4)} to{" "}
                {xrfFe2O3Max == null ? "—" : formatMetric(xrfFe2O3Max, 4)}
              </span>
              <span className="pill">
                Y range: {fe2FeTotalMin == null ? "—" : formatMetric(fe2FeTotalMin, 4)} to{" "}
                {fe2FeTotalMax == null ? "—" : formatMetric(fe2FeTotalMax, 4)}
              </span>
              <span className="pill">{redoxVsXrfFitEquation}</span>
              <span className="pill">
                R²: {redoxVsXrfRegression?.r2 == null ? "—" : formatMetric(redoxVsXrfRegression.r2, 4)}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="card" style={buildProfessionalPlotCardStyle()}>
        {activePlot.hasData ? (
          <Plot
            data={activePlot.data}
            layout={activePlot.layout}
            config={{ responsive: true, displaylogo: false }}
            style={{ width: "100%", height: 420 }}
          />
        ) : (
          <div style={{ color: "var(--text-muted)", padding: "24px 8px" }}>
            {activePlot.emptyMessage}
          </div>
        )}
      </div>
    </div>
  );
}

function ColorReportPanel({
  basisRows,
  hydratedRows,
  onHydrate,
  curveKind,
  onCurveKindChange,
  onExportHtml,
  onExportCsv,
  onExportXlsx
}: {
  basisRows: ColorMeasurement[];
  hydratedRows: ColorMeasurementDetail[];
  onHydrate: () => void;
  curveKind: ColorCurveKind;
  onCurveKindChange: (kind: ColorCurveKind) => void;
  onExportHtml: () => void;
  onExportCsv: () => void;
  onExportXlsx: () => void;
}) {
  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center"
        }}
      >
        <div>
          <h3 className="section-title">Reports</h3>
          <p style={{ margin: "4px 0 0", color: "var(--text-subtle)" }}>
            Generates a print-friendly HTML report and spreadsheet exports from the same compare and
            detail data used in the archive.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select
            className="select"
            value={curveKind}
            onChange={(event) => onCurveKindChange(event.target.value as ColorCurveKind)}
            style={{ minWidth: 220 }}
          >
            {(Object.keys(COLOR_CURVE_LABELS) as ColorCurveKind[]).map((kind) => (
              <option key={kind} value={kind}>
                {COLOR_CURVE_LABELS[kind]}
              </option>
            ))}
          </select>
          <button className="button secondary" onClick={onHydrate} disabled={!basisRows.length}>
            Hydrate Curves
          </button>
          <button className="button secondary export-action" onClick={onExportHtml} disabled={!basisRows.length}>
            Export HTML
          </button>
          <button className="button secondary export-action" onClick={onExportCsv} disabled={!basisRows.length}>
            Export CSV
          </button>
          <button className="button secondary export-xlsx" onClick={onExportXlsx} disabled={!basisRows.length}>
            Export XLSX
          </button>
        </div>
      </div>

      <div className="grid three">
        <div className="card" style={{ background: "var(--surface-elevated)" }}>
          <div className="label">Report Basis</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{basisRows.length}</div>
          <div style={{ color: "var(--text-subtle)", marginTop: 4 }}>
            Rows currently included in the report scope.
          </div>
        </div>
        <div className="card" style={{ background: "var(--surface-elevated)" }}>
          <div className="label">Hydrated Curves</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{hydratedRows.length}</div>
          <div style={{ color: "var(--text-subtle)", marginTop: 4 }}>
            Detail records ready for spectra and raw-curve export.
          </div>
        </div>
        <div className="card" style={{ background: "var(--surface-elevated)" }}>
          <div className="label">Curve Mode</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{COLOR_CURVE_LABELS[curveKind]}</div>
          <div style={{ color: "var(--text-subtle)", marginTop: 4 }}>
            Shared across the plot preview and exports.
          </div>
        </div>
      </div>

      <div className="card" style={{ background: "var(--surface-elevated)" }}>
        <div className="label">Preview Scope</div>
        <div style={{ overflowX: "auto" }}>
          <table className="table">
            <thead>
              <tr>
                <th>Measured At</th>
                <th>Measurement</th>
                <th>Method</th>
                <th>Color</th>
                <th>Brightness</th>
                <th>x / y</th>
                <th>L / a / b</th>
                <th>Wavelength</th>
              </tr>
            </thead>
            <tbody>
              {basisRows.length ? (
                basisRows.slice(0, 12).map((row) => (
                  <tr key={row.id}>
                    <td>{formatAppDateTime(row.measured_at)}</td>
                    <td>{formatReferenceLabel(row)}</td>
                    <td>{row.method_name || "—"}</td>
                    <td>{row.glass_color || "—"}</td>
                    <td>{formatPercent(row.brightness_percent, 2)}</td>
                    <td>{`${formatMetric(row.x_chromaticity, 4)} / ${formatMetric(row.y_chromaticity, 4)}`}</td>
                    <td>{`${formatMetric(row.l_star, 2)} / ${formatMetric(row.a_star, 2)} / ${formatMetric(row.b_star, 2)}`}</td>
                    <td>{formatWavelength(row.dominant_wavelength_nm, row.wavelength_mode)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} style={{ color: "var(--text-muted)" }}>
                    No rows are in scope for reporting.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SyncModal({
  open,
  running,
  error,
  logs,
  progress,
  dryRun,
  clearExisting,
  methodId,
  methods,
  folderFilter,
  paused,
  stopRequested,
  exportingMissingRows,
  onClose,
  onMethodChange,
  onDryRunChange,
  onClearExistingChange,
  onFolderFilterChange,
  onStart,
  onExportMissingRows,
  onPauseToggle,
  onStop,
  mountPoint
}: {
  open: boolean;
  running: boolean;
  error: string | null;
  logs: LogEntry[];
  progress: SyncProgressState;
  dryRun: boolean;
  clearExisting: boolean;
  methodId: string;
  methods: ColorMeasurementMethod[];
  folderFilter: string;
  paused: boolean;
  stopRequested: boolean;
  exportingMissingRows: boolean;
  onClose: () => void;
  onMethodChange: (value: string) => void;
  onDryRunChange: (value: boolean) => void;
  onClearExistingChange: (value: boolean) => void;
  onFolderFilterChange: (value: string) => void;
  onStart: () => void;
  onExportMissingRows: () => void;
  onPauseToggle: () => void;
  onStop: () => void;
  mountPoint: string | null;
}) {
  useEscapeStack(open && !running, onClose);
  if (!open) return null;

  return (
    <div className="modal-backdrop" style={{ zIndex: 2100 }}>
      <div className="modal" style={{ maxWidth: 920 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "flex-start"
          }}
        >
          <div>
            <h3 className="section-title" style={{ marginTop: 0 }}>
              Sync Color Measurements
            </h3>
            <p style={{ margin: "6px 0 0", color: "var(--text-subtle)" }}>
              NAS root: {mountPoint || "Not mounted"}.
            </p>
          </div>
          <button className="button secondary" onClick={onClose} disabled={running}>
            Close
          </button>
        </div>

        <div className="grid two" style={{ marginTop: 16 }}>
          <div>
            <div className="label">Measurement Method</div>
            <select
              className="select"
              value={methodId}
              onChange={(event) => onMethodChange(event.target.value)}
              disabled={running}
            >
              <option value={AUTO_METHOD_ID}>Auto from active Glaschemie trial</option>
              {methods.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="label">Folder Filter</div>
            <input
              className="input"
              placeholder="Optional folder name filter"
              value={folderFilter}
              onChange={(event) => onFolderFilterChange(event.target.value)}
              disabled={running}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
          <label className="pill">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(event) => onDryRunChange(event.target.checked)}
              disabled={running}
            />
            Dry Run
          </label>
          <label
            className="pill"
            style={{
              borderColor: clearExisting ? "var(--status-warning)" : undefined,
              color: clearExisting ? "var(--status-warning)" : undefined
            }}
          >
            <input
              type="checkbox"
              checked={clearExisting}
              onChange={(event) => onClearExistingChange(event.target.checked)}
              disabled={running}
            />
            Clear Existing Before Sync
          </label>
          <span className="pill">
            Folders: {progress.foldersDone} / {progress.foldersTotal}
          </span>
          <span className="pill">
            Files: {progress.totalFilesDone} / {progress.totalFiles}
          </span>
          <span className="pill">Current Folder: {progress.currentFolder || "—"}</span>
        </div>

        {clearExisting && !dryRun && (
          <div className="card" style={{ marginTop: 14, borderColor: "var(--status-warning)" }}>
            <span style={{ color: "var(--status-warning)" }}>
              Existing Color measurements in the current sync scope will be deleted before import.
            </span>
          </div>
        )}

        {error && (
          <div className="card" style={{ marginTop: 14, borderColor: "var(--status-error)" }}>
            <span className="status-error">{error}</span>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
          <button className="button" onClick={onStart} disabled={running || exportingMissingRows}>
            Start Sync
          </button>
          <button
            className="button secondary export-action"
            onClick={onExportMissingRows}
            disabled={running || exportingMissingRows}
          >
            {exportingMissingRows ? "Exporting Missing Rows..." : "Export Missing Thickness Rows"}
          </button>
          <button className="button secondary" onClick={onPauseToggle} disabled={!running}>
            {paused ? "Resume" : "Pause"}
          </button>
          <button className="button secondary" onClick={onStop} disabled={!running || stopRequested}>
            {stopRequested ? "Stopping..." : "Stop"}
          </button>
        </div>

        <div className="card" style={{ marginTop: 16, background: "var(--surface-elevated)" }}>
          <div className="label">Live Log</div>
          <div
            style={{
              minHeight: 220,
              maxHeight: 320,
              overflowY: "auto",
              fontFamily: "SF Mono, Fira Code, monospace",
              fontSize: 12,
              lineHeight: 1.5
            }}
          >
            {logs.length ? (
              logs.map((log, index) => (
                <div key={`${index}-${log.message}`} style={{ color: LOG_TONE_COLORS[log.tone] }}>
                  {log.message}
                </div>
              ))
            ) : (
              <div style={{ color: "var(--text-faint)" }}>No sync output yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ColorDetailModal({
  open,
  measurement,
  detail,
  loading,
  onClose
}: {
  open: boolean;
  measurement: ColorMeasurement | null;
  detail: ColorMeasurementDetail | null;
  loading: boolean;
  onClose: () => void;
}) {
  useEscapeStack(open && !loading, onClose);
  const [detailCurveKind, setDetailCurveKind] = useState<ColorCurveKind>(DEFAULT_COLOR_CURVE_KIND);
  const detailRecord = measurement
    ? detail || {
        ...measurement,
        curves: {}
      }
    : null;
  const availableCurveKinds = detailRecord
    ? (Object.keys(COLOR_CURVE_LABELS) as ColorCurveKind[]).filter(
        (curveKind) => getCurvePoints(detailRecord, curveKind).length
      )
    : [];

  useEffect(() => {
    if (!open || !measurement || !detailRecord) return;
    if (availableCurveKinds.includes(DEFAULT_COLOR_CURVE_KIND)) {
      setDetailCurveKind(DEFAULT_COLOR_CURVE_KIND);
      return;
    }
    setDetailCurveKind(availableCurveKinds[0] || DEFAULT_COLOR_CURVE_KIND);
  }, [availableCurveKinds, detailRecord, measurement?.id, open]);

  if (!open || !measurement || !detailRecord) return null;

  const exportLabel = measurement.measurement_label || measurement.id;
  const detailCurveRows = getCurvePoints(detailRecord, detailCurveKind);

  const canShowLab =
    Number.isFinite(detailRecord.l_star) &&
    Number.isFinite(detailRecord.a_star) &&
    Number.isFinite(detailRecord.b_star);
  const detailViewingConditions = summarizeViewingConditions([detailRecord]);

  const exportRawCurves = (extension: "csv" | "xlsx") => {
    const rows = Object.entries(detailRecord.curves).flatMap(([curveKind, curveRows]) =>
      (curveRows || []).map((row) => [
        curveKind,
        row.wavelength_nm,
        row.value
      ])
    );

    if (extension === "csv") {
      const csv = [["curve_kind", "wavelength_nm", "value"], ...rows]
        .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      downloadText(csv, buildExportFileName(`color_curves_${exportLabel}`, "csv"), "text/csv");
      return;
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.aoa_to_sheet([["curve_kind", "wavelength_nm", "value"], ...rows]),
      "Curves"
    );
    const workbookData = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    downloadBlob(
      new Blob([workbookData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      }),
      buildExportFileName(`color_curves_${exportLabel}`, "xlsx")
    );
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 2200 }}>
      <div className="modal" style={{ width: "min(1180px, 96vw)" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "flex-start"
          }}
        >
          <div>
            <h3 className="section-title" style={{ marginTop: 0 }}>
              {formatReferenceLabel(measurement)}
            </h3>
            <p style={{ margin: "6px 0 0", color: "var(--text-subtle)" }}>
              Measured {formatAppDateTime(measurement.measured_at)} | {measurement.method_name || "Unknown method"}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="button secondary export-action" onClick={() => exportRawCurves("csv")}>
              Export Curves CSV
            </button>
            <button className="button secondary export-xlsx" onClick={() => exportRawCurves("xlsx")}>
              Export Curves XLSX
            </button>
            <button className="button secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {loading && (
          <div className="card" style={{ marginTop: 16 }}>
            Loading curve detail...
          </div>
        )}

        <div className="grid three" style={{ marginTop: 16 }}>
          <MetricCard label="Brightness" value={formatPercent(measurement.brightness_percent, 2)} />
          <MetricCard label="Color" value={measurement.glass_color || "—"} />
          <MetricCard label="Chromaticity" value={`${formatMetric(measurement.x_chromaticity, 4)} / ${formatMetric(measurement.y_chromaticity, 4)}`} />
          <MetricCard label="Wavelength" value={formatWavelength(measurement.dominant_wavelength_nm, measurement.wavelength_mode)} />
          <MetricCard label="Thickness Mean" value={`${formatMetric(measurement.thickness_mean_mm, 3)} mm`} />
          <MetricCard label="Fe2 / Fe Total" value={formatPercent(measurement.fe2_fe_total_percent, 2)} />
          <MetricCard label="Calculation Version" value={measurement.calculation_version} />
        </div>

        <div className="card" style={{ ...buildProfessionalPlotCardStyle(), marginTop: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 8
            }}
          >
            <div className="label">Spectra</div>
            <select
              className="select"
              value={detailCurveKind}
              onChange={(event) => setDetailCurveKind(event.target.value as ColorCurveKind)}
              style={{ minWidth: 220 }}
              disabled={!availableCurveKinds.length}
            >
              {availableCurveKinds.length ? (
                availableCurveKinds.map((curveKind) => (
                  <option key={curveKind} value={curveKind}>
                    {COLOR_CURVE_LABELS[curveKind]}
                  </option>
                ))
              ) : (
                <option value={detailCurveKind}>No curves available</option>
              )}
            </select>
          </div>
          {detailCurveRows.length ? (
            <Plot
              data={[
                {
                  x: detailCurveRows.map((row) => row.wavelength_nm),
                  y: detailCurveRows.map((row) => row.value),
                  type: "scatter",
                  mode: "lines",
                  name: COLOR_CURVE_LABELS[detailCurveKind],
                  hovertemplate: `${COLOR_CURVE_LABELS[detailCurveKind]}<br>%{x:.1f} nm<br>%{y:.4f}<extra></extra>`,
                  line: {
                    color: colorForIndex(0),
                    width: 2.8
                  }
                }
              ]}
              layout={buildSpectrumLayout(COLOR_CURVE_LABELS[detailCurveKind])}
              config={{ responsive: true, displaylogo: false }}
              style={{ width: "100%", height: 360 }}
            />
          ) : (
            <div style={{ color: "var(--text-muted)", padding: "24px 8px" }}>
              No spectral curve is available for the selected view.
            </div>
          )}
        </div>

        <div className="grid two" style={{ marginTop: 16 }}>
          <div className="card" style={buildProfessionalPlotCardStyle()}>
            <div className="label">Chromaticity Diagram ({detailViewingConditions.label})</div>
            <Plot
              data={buildChromaticityPlotData([detailRecord], null, "always")}
              layout={buildChromaticityLayout([detailRecord], null, "always")}
              config={{ responsive: true, displaylogo: false }}
              style={{ width: "100%", height: 380 }}
            />
          </div>

          <div className="card" style={buildProfessionalPlotCardStyle()}>
            <div className="label">L*a*b* Color Space ({detailViewingConditions.label})</div>
            {canShowLab ? (
              <Plot
                data={buildLabPlotData([detailRecord], null, "always")}
                layout={buildLabLayout([detailRecord], null, "always")}
                config={{ responsive: true, displaylogo: false }}
                style={{ width: "100%", height: 380 }}
              />
            ) : (
              <div style={{ color: "var(--text-muted)", padding: "24px 8px" }}>
                CIELAB values are not available for this measurement yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="card" style={{ background: "var(--surface-elevated)" }}>
      <div className="label">{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function ColorAdminPanel({
  observers,
  illuminants,
  transmittanceModes,
  reflectionModels,
  glassProperties,
  trialDurations,
  colorTrialMappings,
  methods,
  onObserversChanged,
  onIlluminantsChanged,
  onTransmittanceModesChanged,
  onReflectionModelsChanged,
  onMethodsChanged,
  onColorTrialMappingsChanged
}: {
  observers: ColorObserver[];
  illuminants: ColorIlluminant[];
  transmittanceModes: ColorTransmittanceMode[];
  reflectionModels: ColorReflectionCorrectionModel[];
  glassProperties: ColorGlassProperty[];
  trialDurations: ColorTrialDurationRow[];
  colorTrialMappings: ColorTrialColorMapping[];
  methods: ColorMeasurementMethod[];
  onObserversChanged: (fn: () => Promise<void>) => Promise<void>;
  onIlluminantsChanged: (fn: () => Promise<void>) => Promise<void>;
  onTransmittanceModesChanged: (fn: () => Promise<void>) => Promise<void>;
  onReflectionModelsChanged: (fn: () => Promise<void>) => Promise<void>;
  onMethodsChanged: (fn: () => Promise<void>) => Promise<void>;
  onColorTrialMappingsChanged: (fn: () => Promise<void>) => Promise<void>;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="card">
        <h3 className="section-title">Admin</h3>
        <p style={{ margin: "6px 0 0", color: "var(--text-subtle)" }}>
          Core CRUD covers methods, observers, illuminants, transmittance modes, and
          reflection-correction models. Optical properties stay read-only in this first implementation.
        </p>
      </div>

      <ColorTrialMappingSection
        trialDurations={trialDurations}
        mappings={colorTrialMappings}
        methods={methods}
        onMappingsChanged={onColorTrialMappingsChanged}
      />

      <GenericAdminSection<ColorObserver, ColorObserverPayload>
        title="Observers"
        description="Reusable observer definitions for the Color module."
        rows={observers}
        fields={[
          { key: "code", label: "Code", type: "text", required: true },
          { key: "name", label: "Name", type: "text", required: true },
          { key: "observer_angle_deg", label: "Observer Angle (deg)", type: "number", required: true, step: "0.1" },
          { key: "standard_reference", label: "Standard Reference", type: "text", required: true },
          { key: "notes", label: "Notes", type: "textarea" }
        ]}
        columns={[
          { label: "Code", render: (row) => row.code },
          { label: "Name", render: (row) => row.name },
          { label: "Angle", render: (row) => row.observer_angle_deg.toFixed(1) },
          { label: "Standard", render: (row) => row.standard_reference }
        ]}
        createEmpty={() => ({
          code: "",
          name: "",
          observer_angle_deg: "2",
          standard_reference: "",
          notes: ""
        })}
        rowToDraft={(row) => ({
          code: row.code,
          name: row.name,
          observer_angle_deg: String(row.observer_angle_deg),
          standard_reference: row.standard_reference,
          notes: row.notes || ""
        })}
        toPayload={(draft) => ({
          code: String(draft.code || "").trim(),
          name: String(draft.name || "").trim(),
          observer_angle_deg: Number(draft.observer_angle_deg),
          standard_reference: String(draft.standard_reference || "").trim(),
          notes: String(draft.notes || "").trim() || null
        })}
        onCreate={(payload) => onObserversChanged(() => createColorObserver(payload).then(() => undefined))}
        onUpdate={(id, payload) => onObserversChanged(() => updateColorObserver(id, payload).then(() => undefined))}
        onDelete={(id) => onObserversChanged(() => deleteColorObserver(id))}
      />

      <GenericAdminSection<ColorIlluminant, ColorIlluminantPayload>
        title="Illuminants"
        description="Reusable illuminant definitions used by measurement methods."
        rows={illuminants}
        fields={[
          { key: "code", label: "Code", type: "text", required: true },
          { key: "name", label: "Name", type: "text", required: true },
          { key: "standard_reference", label: "Standard Reference", type: "text", required: true },
          { key: "is_historical", label: "Historical", type: "checkbox" },
          { key: "notes", label: "Notes", type: "textarea" }
        ]}
        columns={[
          { label: "Code", render: (row) => row.code },
          { label: "Name", render: (row) => row.name },
          { label: "Standard", render: (row) => row.standard_reference },
          { label: "Historical", render: (row) => (row.is_historical ? "Yes" : "No") }
        ]}
        createEmpty={() => ({
          code: "",
          name: "",
          standard_reference: "",
          is_historical: false,
          notes: ""
        })}
        rowToDraft={(row) => ({
          code: row.code,
          name: row.name,
          standard_reference: row.standard_reference,
          is_historical: row.is_historical,
          notes: row.notes || ""
        })}
        toPayload={(draft) => ({
          code: String(draft.code || "").trim(),
          name: String(draft.name || "").trim(),
          standard_reference: String(draft.standard_reference || "").trim(),
          is_historical: Boolean(draft.is_historical),
          notes: String(draft.notes || "").trim() || null
        })}
        onCreate={(payload) => onIlluminantsChanged(() => createColorIlluminant(payload).then(() => undefined))}
        onUpdate={(id, payload) => onIlluminantsChanged(() => updateColorIlluminant(id, payload).then(() => undefined))}
        onDelete={(id) => onIlluminantsChanged(() => deleteColorIlluminant(id))}
      />

      <GenericAdminSection<ColorTransmittanceMode, ColorTransmittanceModePayload>
        title="Transmittance Modes"
        description="Lookup rows describing the measurement geometry or interpretation."
        rows={transmittanceModes}
        fields={[
          { key: "code", label: "Code", type: "text", required: true },
          { key: "name", label: "Name", type: "text", required: true },
          { key: "description", label: "Description", type: "textarea", required: true },
          { key: "notes", label: "Notes", type: "textarea" }
        ]}
        columns={[
          { label: "Code", render: (row) => row.code },
          { label: "Name", render: (row) => row.name },
          { label: "Description", render: (row) => row.description }
        ]}
        createEmpty={() => ({
          code: "",
          name: "",
          description: "",
          notes: ""
        })}
        rowToDraft={(row) => ({
          code: row.code,
          name: row.name,
          description: row.description,
          notes: row.notes || ""
        })}
        toPayload={(draft) => ({
          code: String(draft.code || "").trim(),
          name: String(draft.name || "").trim(),
          description: String(draft.description || "").trim(),
          notes: String(draft.notes || "").trim() || null
        })}
        onCreate={(payload) =>
          onTransmittanceModesChanged(() => createColorTransmittanceMode(payload).then(() => undefined))
        }
        onUpdate={(id, payload) =>
          onTransmittanceModesChanged(() => updateColorTransmittanceMode(id, payload).then(() => undefined))
        }
        onDelete={(id) => onTransmittanceModesChanged(() => deleteColorTransmittanceMode(id))}
      />

      <GenericAdminSection<ColorReflectionCorrectionModel, ColorReflectionCorrectionModelPayload>
        title="Reflection Correction Models"
        description="Lookup rows for reflection-loss handling."
        rows={reflectionModels}
        fields={[
          { key: "code", label: "Code", type: "text", required: true },
          { key: "name", label: "Name", type: "text", required: true },
          { key: "description", label: "Description", type: "textarea", required: true },
          { key: "requires_reflectivity_pct", label: "Requires Reflectivity %", type: "checkbox" },
          { key: "notes", label: "Notes", type: "textarea" }
        ]}
        columns={[
          { label: "Code", render: (row) => row.code },
          { label: "Name", render: (row) => row.name },
          { label: "Requires R%", render: (row) => (row.requires_reflectivity_pct ? "Yes" : "No") },
          { label: "Description", render: (row) => row.description }
        ]}
        createEmpty={() => ({
          code: "",
          name: "",
          description: "",
          requires_reflectivity_pct: false,
          notes: ""
        })}
        rowToDraft={(row) => ({
          code: row.code,
          name: row.name,
          description: row.description,
          requires_reflectivity_pct: row.requires_reflectivity_pct,
          notes: row.notes || ""
        })}
        toPayload={(draft) => ({
          code: String(draft.code || "").trim(),
          name: String(draft.name || "").trim(),
          description: String(draft.description || "").trim(),
          requires_reflectivity_pct: Boolean(draft.requires_reflectivity_pct),
          notes: String(draft.notes || "").trim() || null
        })}
        onCreate={(payload) =>
          onReflectionModelsChanged(() => createColorReflectionCorrectionModel(payload).then(() => undefined))
        }
        onUpdate={(id, payload) =>
          onReflectionModelsChanged(() => updateColorReflectionCorrectionModel(id, payload).then(() => undefined))
        }
        onDelete={(id) => onReflectionModelsChanged(() => deleteColorReflectionCorrectionModel(id))}
      />

      <GenericAdminSection<ColorMeasurementMethod, ColorMeasurementMethodPayload>
        title="Measurement Methods"
        description="Reusable calculation and measurement method definitions."
        rows={methods}
        fields={[
          { key: "name", label: "Name", type: "text", required: true },
          { key: "description", label: "Description", type: "textarea" },
          { key: "wavelength_min_nm", label: "Wavelength Min (nm)", type: "number", required: true, step: "1" },
          { key: "wavelength_max_nm", label: "Wavelength Max (nm)", type: "number", required: true, step: "1" },
          { key: "wavelength_step_nm", label: "Wavelength Step (nm)", type: "number", required: true, step: "0.1" },
          {
            key: "transmittance_mode_id",
            label: "Transmittance Mode",
            type: "select",
            required: true,
            options: transmittanceModes.map((row) => ({ value: row.id, label: `${row.code} | ${row.name}` }))
          },
          {
            key: "reflection_correction_model_id",
            label: "Reflection Model",
            type: "select",
            required: true,
            options: reflectionModels.map((row) => ({ value: row.id, label: `${row.code} | ${row.name}` }))
          },
          { key: "surface_reflectivity_pct_default", label: "Surface Reflectivity % Default", type: "number", step: "0.01" },
          { key: "standard_thickness_mm", label: "Standard Thickness (mm)", type: "number", required: true, step: "0.001" },
          {
            key: "observer_id",
            label: "Observer",
            type: "select",
            required: true,
            options: observers.map((row) => ({ value: row.id, label: `${row.code} | ${row.name}` }))
          },
          {
            key: "illuminant_id",
            label: "Illuminant",
            type: "select",
            required: true,
            options: illuminants.map((row) => ({ value: row.id, label: `${row.code} | ${row.name}` }))
          },
          { key: "reference_medium_name", label: "Reference Medium Name", type: "text" },
          { key: "reference_medium_notes", label: "Reference Medium Notes", type: "textarea" },
          { key: "is_active", label: "Active", type: "checkbox" }
        ]}
        columns={[
          { label: "Name", render: (row) => row.name },
          { label: "Range", render: (row) => `${row.wavelength_min_nm}-${row.wavelength_max_nm} nm` },
          { label: "Step", render: (row) => row.wavelength_step_nm },
          { label: "Observer", render: (row) => row.observer_code || row.observer_id },
          { label: "Illuminant", render: (row) => row.illuminant_code || row.illuminant_id },
          { label: "Active", render: (row) => (row.is_active ? "Yes" : "No") }
        ]}
        createEmpty={() => ({
          name: "",
          description: "",
          wavelength_min_nm: "380",
          wavelength_max_nm: "780",
          wavelength_step_nm: "5",
          transmittance_mode_id: transmittanceModes[0]?.id || "",
          reflection_correction_model_id: reflectionModels[0]?.id || "",
          surface_reflectivity_pct_default: "",
          standard_thickness_mm: "5",
          observer_id: observers[0]?.id || "",
          illuminant_id: illuminants[0]?.id || "",
          reference_medium_name: "",
          reference_medium_notes: "",
          is_active: true
        })}
        rowToDraft={(row) => ({
          name: row.name,
          description: row.description || "",
          wavelength_min_nm: String(row.wavelength_min_nm),
          wavelength_max_nm: String(row.wavelength_max_nm),
          wavelength_step_nm: String(row.wavelength_step_nm),
          transmittance_mode_id: row.transmittance_mode_id,
          reflection_correction_model_id: row.reflection_correction_model_id,
          surface_reflectivity_pct_default:
            row.surface_reflectivity_pct_default == null ? "" : String(row.surface_reflectivity_pct_default),
          standard_thickness_mm: String(row.standard_thickness_mm),
          observer_id: row.observer_id,
          illuminant_id: row.illuminant_id,
          reference_medium_name: row.reference_medium_name || "",
          reference_medium_notes: row.reference_medium_notes || "",
          is_active: row.is_active
        })}
        toPayload={(draft) => ({
          name: String(draft.name || "").trim(),
          description: String(draft.description || "").trim() || null,
          wavelength_min_nm: Number(draft.wavelength_min_nm),
          wavelength_max_nm: Number(draft.wavelength_max_nm),
          wavelength_step_nm: Number(draft.wavelength_step_nm),
          transmittance_mode_id: String(draft.transmittance_mode_id || ""),
          reflection_correction_model_id: String(draft.reflection_correction_model_id || ""),
          surface_reflectivity_pct_default:
            String(draft.surface_reflectivity_pct_default || "").trim() === ""
              ? null
              : Number(draft.surface_reflectivity_pct_default),
          standard_thickness_mm: Number(draft.standard_thickness_mm),
          observer_id: String(draft.observer_id || ""),
          illuminant_id: String(draft.illuminant_id || ""),
          reference_medium_name: String(draft.reference_medium_name || "").trim() || null,
          reference_medium_notes: String(draft.reference_medium_notes || "").trim() || null,
          is_active: Boolean(draft.is_active)
        })}
        onCreate={(payload) =>
          onMethodsChanged(() => createColorMeasurementMethod(payload).then(() => undefined))
        }
        onUpdate={(id, payload) =>
          onMethodsChanged(() => updateColorMeasurementMethod(id, payload).then(() => undefined))
        }
        onDelete={(id) => onMethodsChanged(() => deleteColorMeasurementMethod(id))}
      />

      <div className="card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap"
          }}
        >
          <div>
            <h3 className="section-title">Optical Properties</h3>
            <p style={{ margin: "6px 0 0", color: "var(--text-subtle)" }}>
              Read-only reference data derived from the optical profiles used in
              [plot_abs_spectra.py](/Users/manotes/Documents/IPGR/ZeroCO2/interface-mb/vendor/color/prototypes/plot_abs_spectra.py).
            </p>
          </div>
          <span className="pill">Rows: {glassProperties.length}</span>
        </div>
        <div style={{ overflowX: "auto", marginTop: 12 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Description</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {glassProperties.map((row) => (
                <tr key={row.id}>
                  <td>{row.code}</td>
                  <td>{row.name}</td>
                  <td>{row.description || "—"}</td>
                  <td>{row.is_active === false ? "No" : "Yes"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ColorTrialMappingSection({
  trialDurations,
  mappings,
  methods,
  onMappingsChanged
}: {
  trialDurations: ColorTrialDurationRow[];
  mappings: ColorTrialColorMapping[];
  methods: ColorMeasurementMethod[];
  onMappingsChanged: (fn: () => Promise<void>) => Promise<void>;
}) {
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [busyTrialId, setBusyTrialId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mappingByTrialId = useMemo(
    () => new Map(mappings.map((row) => [row.trial_duration_id, row] as const)),
    [mappings]
  );
  const knownColors = useMemo(
    () =>
      Array.from(
        new Set([
          ...mappings.map((row) => row.glass_color?.trim()).filter(Boolean),
          ...methods.map((row) => row.name?.trim()).filter(Boolean)
        ] as string[])
      ).sort((left, right) => left.localeCompare(right)),
    [mappings, methods]
  );
  const glaschemieTrials = useMemo(
    () =>
      trialDurations
        .filter((row) => row.trial_type.toLowerCase() === "glaschemie")
        .sort((left, right) => Date.parse(right.start_time) - Date.parse(left.start_time)),
    [trialDurations]
  );

  const draftValueFor = (trialId: number) =>
    drafts[trialId] ?? mappingByTrialId.get(trialId)?.glass_color ?? "";

  const handleSave = async (trial: ColorTrialDurationRow) => {
    const value = draftValueFor(trial.id).trim();
    const existing = mappingByTrialId.get(trial.id);
    setBusyTrialId(trial.id);
    setError(null);
    try {
      if (!value) {
        if (existing) {
          await onMappingsChanged(() => deleteColorTrialColorMapping(existing.id));
        }
      } else if (existing) {
        const payload: Partial<ColorTrialColorMappingPayload> = {
          trial_duration_id: trial.id,
          glass_color: value,
          notes: existing.notes ?? null
        };
        await onMappingsChanged(() => updateColorTrialColorMapping(existing.id, payload).then(() => undefined));
      } else {
        const payload: ColorTrialColorMappingPayload = {
          trial_duration_id: trial.id,
          glass_color: value,
          notes: null
        };
        await onMappingsChanged(() => createColorTrialColorMapping(payload).then(() => undefined));
      }
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[trial.id];
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Color mapping save failed");
    } finally {
      setBusyTrialId(null);
    }
  };

  const handleReset = (trialId: number) => {
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[trialId];
      return next;
    });
  };

  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center"
        }}
      >
        <div>
          <h3 className="section-title">Glaschemie Color Mapping</h3>
          <p style={{ margin: "6px 0 0", color: "var(--text-subtle)" }}>
            Assign the sample color used by Auto sync for each Glaschemie trial.
          </p>
        </div>
        <span className="pill">Mapped: {mappings.length}</span>
      </div>

      {error && <div className="status-error">{error}</div>}

      <datalist id="color-trial-color-options">
        {knownColors.map((value) => (
          <option key={value} value={value} />
        ))}
      </datalist>

      <div style={{ overflowX: "auto", maxHeight: 460, overflowY: "auto" }}>
        <table className="table">
          <thead>
            <tr>
              <th>Trial</th>
              <th>Start</th>
              <th>End</th>
              <th>Color</th>
              <th>Auto Method</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {glaschemieTrials.length ? (
              glaschemieTrials.map((trial) => {
                const existing = mappingByTrialId.get(trial.id);
                const draftValue = draftValueFor(trial.id);
                const dirty = draftValue !== (existing?.glass_color ?? "");
                const busy = busyTrialId === trial.id;
                const matchingMethod = resolveAutoMeasurementMethod(methods, draftValue.trim() || null);
                return (
                  <tr key={trial.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{trial.descriptive_name}</div>
                      <div style={{ color: "var(--text-faint)", fontSize: 12 }}>ID {trial.id}</div>
                    </td>
                    <td>{formatAppDateTime(trial.start_time)}</td>
                    <td>{trial.end_time ? formatAppDateTime(trial.end_time) : "Open"}</td>
                    <td style={{ minWidth: 220 }}>
                      <input
                        className="input"
                        list="color-trial-color-options"
                        placeholder="Enter color label"
                        value={draftValue}
                        disabled={busy}
                        onChange={(event) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [trial.id]: event.target.value
                          }))
                        }
                      />
                    </td>
                    <td>{matchingMethod?.name || "No matching method"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <button
                          className="button secondary"
                          onClick={() => handleSave(trial)}
                          disabled={busy || !dirty}
                        >
                          Save
                        </button>
                        <button
                          className="button secondary"
                          onClick={() => handleReset(trial.id)}
                          disabled={busy || !dirty}
                        >
                          Reset
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} style={{ color: "var(--text-muted)" }}>
                  No Glaschemie trials are available from trial_duration.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GenericAdminSection<Row extends { id: string }, Payload>({
  title,
  description,
  rows,
  fields,
  columns,
  createEmpty,
  rowToDraft,
  toPayload,
  onCreate,
  onUpdate,
  onDelete
}: {
  title: string;
  description: string;
  rows: Row[];
  fields: AdminFieldConfig[];
  columns: AdminColumn<Row>[];
  createEmpty: () => Record<string, unknown>;
  rowToDraft: (row: Row) => Record<string, unknown>;
  toPayload: (draft: Record<string, unknown>) => Payload;
  onCreate: (payload: Payload) => Promise<void>;
  onUpdate: (id: string, payload: Payload) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>(createEmpty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const beginCreate = () => {
    setEditingId(null);
    setDraft(createEmpty());
    setError(null);
  };

  const beginEdit = (row: Row) => {
    setEditingId(row.id);
    setDraft(rowToDraft(row));
    setError(null);
  };

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = toPayload(draft);
      if (editingId) {
        await onUpdate(editingId, payload);
      } else {
        await onCreate(payload);
      }
      setEditingId(null);
      setDraft(createEmpty());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!onDelete) return;
    if (!window.confirm(`Delete this ${title.toLowerCase().slice(0, -1)}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await onDelete(id);
      if (editingId === id) {
        setEditingId(null);
        setDraft(createEmpty());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center"
        }}
      >
        <div>
          <h3 className="section-title">{title}</h3>
          <p style={{ margin: "6px 0 0", color: "var(--text-subtle)" }}>{description}</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <span className="pill">Rows: {rows.length}</span>
          <button className="button secondary" onClick={beginCreate} disabled={busy}>
            New
          </button>
        </div>
      </div>

      {error && <div className="status-error">{error}</div>}

      <div className="grid two">
        <div className="card" style={{ background: "var(--surface-elevated)" }}>
          <div className="label">{editingId ? `Edit ${title.slice(0, -1)}` : `Create ${title.slice(0, -1)}`}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {fields.map((field) => (
              <label key={field.key}>
                <div className="label">{field.label}</div>
                {field.type === "textarea" ? (
                  <textarea
                    className="input"
                    value={String(draft[field.key] ?? "")}
                    placeholder={field.placeholder}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, [field.key]: event.target.value }))
                    }
                    rows={4}
                  />
                ) : field.type === "checkbox" ? (
                  <input
                    type="checkbox"
                    checked={Boolean(draft[field.key])}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, [field.key]: event.target.checked }))
                    }
                  />
                ) : field.type === "select" ? (
                  <select
                    className="select"
                    value={String(draft[field.key] ?? "")}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, [field.key]: event.target.value }))
                    }
                  >
                    {!field.required && <option value="">None</option>}
                    {(field.options || []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="input"
                    type={field.type}
                    step={field.step}
                    placeholder={field.placeholder}
                    value={String(draft[field.key] ?? "")}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, [field.key]: event.target.value }))
                    }
                  />
                )}
              </label>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <button className="button" onClick={handleSave} disabled={busy}>
              {editingId ? "Update" : "Create"}
            </button>
            <button className="button secondary" onClick={beginCreate} disabled={busy}>
              Reset
            </button>
          </div>
        </div>

        <div className="card" style={{ background: "var(--surface-elevated)" }}>
          <div className="label">Existing Rows</div>
          <div style={{ overflowX: "auto", maxHeight: 420, overflowY: "auto" }}>
            <table className="table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.label}>{column.label}</th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    {columns.map((column) => (
                      <td key={column.label}>{column.render(row)}</td>
                    ))}
                    <td>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="button secondary" onClick={() => beginEdit(row)} disabled={busy}>
                          Edit
                        </button>
                        {onDelete && (
                          <button className="button secondary" onClick={() => handleDelete(row.id)} disabled={busy}>
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={columns.length + 1} style={{ color: "var(--text-muted)" }}>
                      No rows available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function numberOrNull(value: unknown) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
}
